<template>
	<InvoiceCreatorLayout v-if="ready" class="client-document-editor-layout document-editor--refreshed" :class="{ 'proposal-editor-layout': isProposal, 'assignment-editor-layout': isAssignmentConfirmation }" :title="pageTitle" @back="navigateBack">
		<template #details>
			<section v-if="isQuotation" id="quotation-sec-details" class="document-details-panel quotation-editor-panel">
				<div class="editor-details-grid">
					<div class="form-group">
						<label for="quotation-business-name">Business name</label>
						<input
							id="quotation-business-name"
							:value="businessName"
							type="text"
							disabled
							class="field-input disabled-input"
						/>
					</div>
					<label class="form-group">
						<span>Quotation No.</span>
						<input v-model="form.content.quotationNumber" type="text" class="field-input" />
					</label>
					<div class="form-group quotation-date-field">
						<span>Date</span>
						<DateRangePicker
							mode="single"
							use-fixed
							:date-from="form.content.quotationDate"
							trigger-label="Quotation date"
							placeholder="Select date"
							@update="({ dateFrom }) => form.content.quotationDate = dateFrom"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
						</DateRangePicker>
					</div>
					<label class="form-group">
						<span>Reference</span>
						<input v-model="form.content.reference" type="text" class="field-input" />
					</label>
					<div class="form-group quotation-cleaning-field quotation-catalog-field">
						<PresetTextInput
							:model-value="form.content.subtitle"
							:options="quotationCleaningStyleOptions"
							:save-options="saveQuotationCleaningStyleOptions"
							label="Cleaning style"
							placeholder="Enter or select a cleaning style"
							manage-title="Manage cleaning style catalog"
							manage-description=""
							manage-button-label="Manage cleaning style catalog"
							manager-close-label="Close cleaning style catalog"
							option-label="Cleaning style option"
							remove-option-label="Remove cleaning style option"
							save-error-message="Could not save cleaning style values. Please try again."
							@update:model-value="onQuotationCleaningStyleChanged"
							@select="onQuotationCleaningStyleSelected"
						/>
					</div>
				</div>

				<div id="quotation-sec-client" class="section-heading section-heading--compact outline-anchor">
					<h2>Client and service</h2>
					<button v-if="sectionImportUndo?.sectionId === 'client'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
						<AppointmentIcon name="undo" :size="18" />
						<span class="action-label">Undo import</span>
					</button>
					<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('client')" :aria-label="sectionImportTitle('client')" @click="openSectionImport('client', $event)">
						<AppointmentIcon name="assign" :size="18" />
						<span class="action-label">Import</span>
					</button>
				</div>
				<div class="quotation-field-grid quotation-field-grid--grouped">
					<label
						class="form-group quotation-client-field"
						v-tooltip="() => form.content.clientName"
					>
						<span>Client</span>
						<div class="document-catalog-control">
							<CatalogSearchInput
								use-fixed
								:modelValue="form.content.clientId"
								:options="clientOptions"
								:fallbackLabel="form.content.clientName"
								placeholder="Select client"
								@update:modelValue="onQuotationClientSelected"
							/>
							<AppointmentIcon name="chevron-down" :size="14" class="document-catalog-chevron" />
						</div>
					</label>
					<label
						class="form-group quotation-service-field"
						v-tooltip="() => form.content.serviceDetails.serviceType"
					>
						<span>Service Type</span>
						<input
							:value="form.content.serviceDetails.serviceType"
							type="text"
							class="field-input"
							placeholder="Enter service type"
							@input="onQuotationServiceTypeChanged($event.target.value)"
						/>
					</label>
					<div
						class="form-group quotation-catalog-field quotation-property-field"
						v-tooltip="() => form.content.serviceDetails.property"
					>
						<PresetTextInput
							:model-value="form.content.serviceDetails.property"
							:options="quotationPropertyOptions"
							:save-options="saveQuotationPropertyOptions"
							:max-length="MAX_QUOTATION_PROPERTY_LENGTH"
							multiline
							:rows="3"
							label="Property Description"
							placeholder="Enter or select a property description"
							manage-title="Manage property catalog"
							manage-description=""
							manage-button-label="Manage property catalog"
							manager-close-label="Close property catalog"
							option-label="Property option"
							remove-option-label="Remove property option"
							save-error-message="Could not save property values. Please try again."
							@update:model-value="onQuotationPropertyChanged"
						/>
					</div>
					<label
						class="form-group quotation-address-field quotation-client-address-field"
						v-tooltip="() => form.content.clientAddress"
					>
						<span>Client address</span>
						<textarea v-textarea-autosize v-model="form.content.clientAddress" rows="2" class="table-textarea compact-textarea"></textarea>
					</label>
					<div
						class="form-group quotation-address-field quotation-catalog-field quotation-site-address-field"
						v-tooltip="() => form.content.serviceDetails.siteAddress"
					>
						<PresetTextInput
							:model-value="form.content.serviceDetails.siteAddress"
							:options="quotationSiteAddressOptions"
							:save-options="saveQuotationSiteAddressOptions"
							:max-length="MAX_QUOTATION_SITE_ADDRESS_LENGTH"
							multiline
							:rows="2"
							label="Site Address"
							placeholder="Enter or select a site address"
							manage-title="Manage site address catalog"
							manage-description=""
							manage-button-label="Manage site address catalog"
							manager-close-label="Close site address catalog"
							option-label="Site address option"
							remove-option-label="Remove site address option"
							save-error-message="Could not save site address values. Please try again."
							@update:model-value="onQuotationSiteAddressChanged"
						/>
					</div>
					<label class="form-group quotation-scope-of-work-field">
						<span>Scope of work</span>
						<textarea
							v-textarea-autosize
							v-model="form.content.serviceDetails.scopeOfWork"
							rows="3"
							class="table-textarea compact-textarea"
							placeholder="Describe the scope of work"
						></textarea>
					</label>
				</div>
			</section>

			<section v-else-if="isAssignmentConfirmation" id="assignment-sec-details" class="document-details-panel assignment-editor-panel">
				<div class="assignment-details-grid editor-details-grid">
					<div class="form-group">
						<label for="assignment-business-name">Business name</label>
						<input
							id="assignment-business-name"
							:value="businessName"
							type="text"
							disabled
							class="field-input disabled-input"
						/>
					</div>
					<label class="form-group">
						<span>Reference</span>
						<input v-model="form.content.reference" type="text" class="field-input" />
					</label>
					<div class="form-group">
						<span>Commence date</span>
						<DateRangePicker
							mode="single"
							use-fixed
							:date-from="form.content.commenceDate"
							trigger-label="Commence date"
							placeholder="Select date"
							@update="({ dateFrom }) => form.content.commenceDate = dateFrom"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
						</DateRangePicker>
					</div>
					<div class="form-group assignment-catalog-field">
						<PresetTextInput
							:model-value="form.content.subtitle"
							:options="assignmentSubtitleOptions"
							:save-options="saveAssignmentSubtitleOptions"
							manage-description=""
							@update:model-value="onAssignmentSubtitleChanged"
						/>
					</div>
				</div>

				<div class="section-heading section-heading--compact">
					<h2>Assignment setup</h2>
					<button v-if="sectionImportUndo?.sectionId === 'setup'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
						<AppointmentIcon name="undo" :size="18" />
						<span class="action-label">Undo import</span>
					</button>
					<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('setup')" :aria-label="sectionImportTitle('setup')" @click="openSectionImport('setup', $event)">
						<AppointmentIcon name="assign" :size="18" />
						<span class="action-label">Import</span>
					</button>
				</div>
				<div class="assignment-details-grid editor-details-grid">
					<label class="form-group">
						<span>Subcontractor</span>
						<div class="document-catalog-control">
							<CatalogSearchInput
								use-fixed
								:modelValue="form.content.subcontractorId"
								:options="supplierOptions"
								:fallbackLabel="form.content.subcontractorName"
								placeholder="Select subcontractor"
								@update:modelValue="onAssignmentSupplierSelected"
							/>
							<AppointmentIcon name="chevron-down" :size="14" class="document-catalog-chevron" />
						</div>
					</label>
					<label class="form-group">
						<span>Property</span>
						<div class="document-catalog-control">
							<CatalogSearchInput
								use-fixed
								:modelValue="form.content.propertyId"
								:options="buildingOptions"
								:fallbackLabel="form.content.propertyName"
								placeholder="Select property"
								@update:modelValue="onAssignmentPropertySelected"
							/>
							<AppointmentIcon name="chevron-down" :size="14" class="document-catalog-chevron" />
						</div>
					</label>
					<label class="form-group">
						<span>Service</span>
						<div class="document-catalog-control">
							<CatalogSearchInput
								use-fixed
								:modelValue="form.content.serviceId"
								:options="serviceOptions"
								:fallbackLabel="form.content.serviceType"
								placeholder="Select service"
								@update:modelValue="onAssignmentServiceSelected"
							/>
							<AppointmentIcon name="chevron-down" :size="14" class="document-catalog-chevron" />
						</div>
					</label>
					<label class="form-group">
						<span>Subcontractor mobile</span>
						<input v-model="form.content.subcontractorMobile" type="text" class="field-input" />
					</label>
					<label class="form-group assignment-span-2">
						<span>Property address</span>
						<textarea v-textarea-autosize v-model="form.content.propertyAddress" rows="1" class="table-textarea compact-textarea"></textarea>
					</label>
				</div>
			</section>

			<section v-else id="proposal-sec-details" class="document-details-panel proposal-editor-panel">
				<div class="editor-details-grid proposal-details-grid">
					<div class="form-group">
						<label for="proposal-business-name">Business name</label>
						<input
							id="proposal-business-name"
							:value="businessName"
							type="text"
							disabled
							class="field-input disabled-input"
						/>
					</div>
					<label
						class="form-group proposal-client-field"
						v-tooltip="() => form.content.clientName"
					>
						<span>Client</span>
						<div class="document-catalog-control">
							<CatalogSearchInput
								use-fixed
								:modelValue="form.content.clientId"
								:options="clientOptions"
								:fallbackLabel="form.content.clientName"
								placeholder="Select client"
								@update:modelValue="onProposalClientSelected"
							/>
							<AppointmentIcon name="chevron-down" :size="14" class="document-catalog-chevron" />
						</div>
					</label>
					<label class="form-group">
						<span>Reference</span>
						<input v-model="form.content.reference" type="text" class="field-input" />
					</label>
					<div class="form-group">
						<span>Proposal date</span>
						<DateRangePicker
							mode="single"
							use-fixed
							:date-from="form.content.proposalDate"
							trigger-label="Proposal date"
							placeholder="Select date"
							@update="({ dateFrom }) => form.content.proposalDate = dateFrom"
						>
							<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
						</DateRangePicker>
					</div>
				</div>

				<div class="section-heading section-heading--compact">
					<h2>Properties</h2>
					<button class="icon-btn icon-btn--add" type="button" title="Add property" aria-label="Add property" @click="addProperty">
						<AppointmentIcon name="plus" :size="18" />
						<span class="action-label">Add property</span>
					</button>
					<button v-if="sectionImportUndo?.sectionId === 'properties'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
						<AppointmentIcon name="undo" :size="18" />
						<span class="action-label">Undo import</span>
					</button>
					<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('properties')" :aria-label="sectionImportTitle('properties')" @click="openSectionImport('properties', $event)">
						<AppointmentIcon name="assign" :size="18" />
						<span class="action-label">Import</span>
					</button>
				</div>
				<div v-if="form.content.properties.length" class="proposal-properties-list">
					<div
						v-for="(property, index) in form.content.properties"
						:key="`property-${index}`"
						class="proposal-property-entry"
					>
						<label class="form-group">
							<span>Property name</span>
							<div class="document-catalog-control">
								<CatalogSearchInput
									use-fixed
									:modelValue="property.buildingId"
									:options="buildingOptions"
									:fallbackLabel="property.name"
									placeholder="Select building"
									@update:modelValue="onProposalBuildingSelected(property, $event)"
								/>
								<AppointmentIcon name="chevron-down" :size="14" class="document-catalog-chevron" />
							</div>
						</label>
						<label class="form-group">
							<span>Address</span>
							<textarea
								v-textarea-autosize
								v-model="property.address"
								rows="1"
								class="table-textarea compact-textarea proposal-property-address"
							></textarea>
						</label>
						<button
							class="icon-btn icon-btn--danger proposal-property-remove"
							type="button"
							title="Remove property"
							:aria-label="`Remove property ${index + 1}`"
							@click="removeAt(form.content.properties, index)"
						>
							<AppointmentIcon name="trash" :size="18" />
						</button>
					</div>
				</div>
				<p v-else class="proposal-properties-empty">No properties added.</p>
			</section>
		</template>

		<template #items>
			<form v-if="isQuotation" class="proposal-form quotation-form editor-column-form" autocomplete="off" @submit.prevent="handleSave">
				<nav class="editor-outline" aria-label="Document sections">
					<p class="editor-outline__title">On this page</p>
					<button
						v-for="section in outlineSections"
						:key="section.id"
						type="button"
						class="editor-outline__link"
						:class="{ 'is-active': activeOutlineSectionId === section.id }"
						@click="scrollToOutlineSection(section.id)"
					>
						{{ section.label }}
					</button>
				</nav>
				<section id="quotation-sec-schedule" class="editor-section">
					<div class="section-heading">
						<h2>Work Schedule</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add phase" aria-label="Add phase" @click="addQuotationPhase">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add phase</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'schedule'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('schedule')" :aria-label="sectionImportTitle('schedule')" @click="openSectionImport('schedule', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table quotation-schedule-table">
							<thead>
								<tr>
									<th>Phase</th>
									<th>Date</th>
									<th>Cleaning operatives</th>
									<th>Estimated cleaning time</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(phase, index) in form.content.schedule.phases" :key="phase.id || `phase-${index}`">
									<td><input v-model="phase.title" :aria-label="`Phase ${index + 1}`" type="text" class="table-input quotation-short-input" /></td>
									<td>
										<DateRangePicker
											mode="single"
											use-fixed
											:date-from="phase.date"
											:trigger-label="`Phase ${index + 1} date`"
											placeholder="Select date"
											@update="({ dateFrom }) => phase.date = dateFrom"
										>
											<template #trigger-icon><AppointmentIcon name="calendar" :size="14" /></template>
										</DateRangePicker>
									</td>
									<td><input v-model="phase.team" :aria-label="`Cleaning operatives ${index + 1}`" type="text" class="table-input" /></td>
									<td><input v-model="phase.time" :aria-label="`Estimated cleaning time ${index + 1}`" type="text" class="table-input" /></td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove phase" aria-label="Remove phase" @click="removeAt(form.content.schedule.phases, index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section id="quotation-sec-costs" class="editor-section">
					<div class="section-heading">
						<h2>Cost Breakdown</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add cost row" aria-label="Add cost row" @click="addQuotationCostRow">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add row</span>
						</button>
						<button
							ref="quotationCostItemManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage cost item catalog"
							aria-label="Manage cost item catalog"
							@click="openQuotationCostItemManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Manage catalog</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'costs'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('costs')" :aria-label="sectionImportTitle('costs')" @click="openSectionImport('costs', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table quotation-cost-table">
							<thead>
								<tr>
									<th>Item</th>
									<th>Amount</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, index) in form.content.costBreakdown.rows" :key="row.id || `cost-${index}`">
									<td>
										<PresetTextCombobox
											:model-value="row.item"
											:options="quotationCostItemOptions"
											:aria-label="`Cost item ${index + 1}`"
											empty-message="No saved cost items yet"
											placeholder="Enter or select a cost item"
											@update:model-value="onQuotationCostItemChanged(index, $event)"
										/>
									</td>
									<td><input v-model.number="row.amount" :aria-label="`Cost amount ${index + 1}`" type="number" step="0.01" class="table-input quotation-amount-input" /></td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove cost row" aria-label="Remove cost row" @click="removeAt(form.content.costBreakdown.rows, index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<PresetValuesManager
						ref="quotationCostItemManagerRef"
						:options="quotationCostItemOptions"
						title="Manage cost item catalog"
						description=""
						close-label="Close cost item catalog"
						option-label="Cost item option"
						remove-option-label="Remove cost item option"
						:save-options="saveQuotationCostItemOptions"
						save-error-message="Could not save cost item values. Please try again."
					/>

					<div class="pricing-grid quotation-totals-grid">
						<div class="pricing-card">
							<label class="form-group">
								<span>VAT rate</span>
								<input v-model.number="form.content.costBreakdown.vatRate" type="number" step="0.01" min="0" class="field-input" />
							</label>
						</div>
						<div class="pricing-card pricing-card--summary">
							<dl class="pricing-stats">
								<div class="pricing-stat">
									<dt>Estimated total excluding VAT</dt>
									<dd>{{ formatCurrency(quotationTotals.subtotal) }}</dd>
								</div>
								<div class="pricing-stat">
									<dt>VAT</dt>
									<dd>{{ formatCurrency(quotationTotals.vat) }}</dd>
								</div>
								<div class="pricing-stat pricing-stat--total">
									<dt>Estimated total including VAT</dt>
									<dd>{{ formatCurrency(quotationTotals.total) }}</dd>
								</div>
							</dl>
						</div>
					</div>

					<label class="form-group quotation-wide-field">
						<span>Additional cost note</span>
						<textarea v-textarea-autosize v-model="form.content.costBreakdown.additionalTerms" rows="2" class="table-textarea compact-textarea"></textarea>
					</label>
				</section>

				<section id="quotation-sec-payment-terms" class="editor-section">
					<div class="section-heading">
						<h2>Payment Terms</h2>
						<button v-if="sectionImportUndo?.sectionId === 'payment-terms'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('payment-terms')" :aria-label="sectionImportTitle('payment-terms')" @click="openSectionImport('payment-terms', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<label class="form-group quotation-wide-field">
						<span>Payment terms text</span>
						<textarea v-textarea-autosize v-model="form.content.paymentTerms.text" rows="2" class="table-textarea compact-textarea"></textarea>
					</label>
				</section>

				<section id="quotation-sec-acceptance" class="editor-section">
					<div class="section-heading">
						<h2>Acceptance</h2>
						<button v-if="sectionImportUndo?.sectionId === 'acceptance'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('acceptance')" :aria-label="sectionImportTitle('acceptance')" @click="openSectionImport('acceptance', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<label class="form-group quotation-wide-field">
						<span>Acceptance text</span>
						<textarea v-textarea-autosize v-model="form.content.acceptance.text" rows="2" class="table-textarea compact-textarea"></textarea>
					</label>
					<label class="form-group quotation-wide-field">
						<span>Validity text</span>
						<textarea v-textarea-autosize v-model="form.content.acceptance.validityText" rows="2" class="table-textarea compact-textarea"></textarea>
					</label>
				</section>

				<section id="quotation-sec-footer" class="editor-section">
					<div class="section-heading">
						<h2>Document footer</h2>
						<button v-if="sectionImportUndo?.sectionId === 'footer'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('footer')" :aria-label="sectionImportTitle('footer')" @click="openSectionImport('footer', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						v-model="form.content.footerText"
						rows="2"
						class="table-textarea compact-textarea document-footer-textarea"
						aria-label="Document footer text"
					></textarea>
				</section>

				<div class="toolbar-bottom-spacer" aria-hidden="true"></div>
			</form>

			<form v-else-if="isAssignmentConfirmation" class="proposal-form assignment-form editor-column-form" autocomplete="off" @submit.prevent="handleSave">
				<nav class="editor-outline" aria-label="Document sections">
					<p class="editor-outline__title">On this page</p>
					<button
						v-for="section in outlineSections"
						:key="section.id"
						type="button"
						class="editor-outline__link"
						:class="{ 'is-active': activeOutlineSectionId === section.id }"
						@click="scrollToOutlineSection(section.id)"
					>
						{{ section.label }}
					</button>
				</nav>
				<section id="assignment-sec-areas" class="editor-section">
					<div class="section-heading">
						<h2>Service areas</h2>
						<button ref="assignmentServiceAreaAddButtonRef" class="icon-btn icon-btn--add" type="button" title="Add service area" aria-label="Add service area" @click="addAssignmentServiceArea">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add area</span>
						</button>
						<button
							ref="assignmentServiceAreaManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage service area catalog"
							aria-label="Manage service area catalog"
							@click="openAssignmentServiceAreaManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Manage catalog</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'areas'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('areas')" :aria-label="sectionImportTitle('areas')" @click="openSectionImport('areas', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table assignment-list-table">
							<tbody>
								<tr v-for="(area, index) in form.content.scope.serviceAreas" :key="`assignment-area-${index}`">
									<td>
										<PresetTextCombobox
											:ref="(component) => setAssignmentServiceAreaInputRef(component, index)"
											:model-value="area"
											:options="assignmentServiceAreaOptionsForIndex(index)"
											:aria-label="`Service area ${index + 1}`"
											:empty-message="assignmentServiceAreaOptions.length ? 'All saved service areas are already used' : 'No saved service areas yet'"
											placeholder="Enter or select a service area"
											@update:model-value="onAssignmentServiceAreaChanged(index, $event)"
										/>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove service area" :aria-label="`Remove service area ${index + 1}`" @click="removeAssignmentServiceArea(index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<PresetValuesManager
						ref="assignmentServiceAreaManagerRef"
						:options="assignmentServiceAreaOptions"
						title="Manage service area catalog"
						description=""
						close-label="Close service area catalog"
						option-label="Service area option"
						remove-option-label="Remove service area option"
						:save-options="saveAssignmentServiceAreaOptions"
						save-error-message="Could not save service area values. Please try again."
					/>
				</section>

				<section id="assignment-sec-regular-tasks" class="editor-section">
					<div class="section-heading">
						<h2>Regular cleaning tasks</h2>
						<button
							ref="assignmentRegularTasksManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage regular cleaning tasks catalog"
							aria-label="Manage regular cleaning tasks catalog"
							@click="openAssignmentRegularTasksManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Manage catalog</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'regular-tasks'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('regular-tasks')" :aria-label="sectionImportTitle('regular-tasks')" @click="openSectionImport('regular-tasks', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						aria-label="Regular cleaning tasks"
						:value="listToBulletedText(form.content.scope.regularTasks)"
						rows="1"
						class="table-textarea bulleted-textarea assignment-wide-textarea assignment-task-textarea"
						@input="updateBulletedStringList(form.content.scope, 'regularTasks', $event.target.value, $event.target)"
						@keydown.enter="handleBulletedTextareaEnter($event, form.content.scope, 'regularTasks')"
						@keydown.backspace="handleBulletedTextareaBackspace($event, form.content.scope, 'regularTasks')"
						@keydown.delete="handleBulletedTextareaDelete($event, form.content.scope, 'regularTasks')"
					></textarea>
					<PresetTaskListManager
						ref="assignmentRegularTasksManagerRef"
						:options="assignmentRegularTasksTemplate"
						title="Manage regular cleaning tasks catalog"
						description=""
						:show-help="false"
						textarea-label="Regular cleaning tasks catalog"
						close-label="Close regular cleaning tasks catalog"
						:save-options="saveAssignmentRegularTasksTemplate"
						save-error-message="Could not save the regular cleaning tasks catalog. Please try again."
					/>
				</section>

				<section id="assignment-sec-monthly-tasks" class="editor-section">
					<div class="section-heading assignment-optional-section-heading">
						<h2>Monthly tasks</h2>
						<div class="assignment-optional-toggle">
							<span class="assignment-optional-toggle-label">Optional</span>
							<button
								id="assignment-monthly-tasks-toggle"
								type="button"
								class="assignment-section-toggle"
								:class="{ 'is-on': form.content.scope.monthlyTasksEnabled }"
								role="switch"
								:aria-checked="form.content.scope.monthlyTasksEnabled ? 'true' : 'false'"
								aria-label="Include monthly tasks in document"
								@click="toggleAssignmentMonthlyTasks"
							>
								<span class="assignment-section-toggle-thumb" aria-hidden="true"></span>
							</button>
						</div>
						<button
							ref="assignmentMonthlyTasksManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage monthly tasks catalog"
							aria-label="Manage monthly tasks catalog"
							@click="openAssignmentMonthlyTasksManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Manage catalog</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'monthly-tasks'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('monthly-tasks')" :aria-label="sectionImportTitle('monthly-tasks')" @click="openSectionImport('monthly-tasks', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-if="form.content.scope.monthlyTasksEnabled"
						v-textarea-autosize
						aria-label="Monthly tasks"
						:value="listToBulletedText(form.content.scope.monthlyTasks)"
						rows="1"
						class="table-textarea bulleted-textarea assignment-wide-textarea assignment-task-textarea"
						@input="updateBulletedStringList(form.content.scope, 'monthlyTasks', $event.target.value, $event.target)"
						@keydown.enter="handleBulletedTextareaEnter($event, form.content.scope, 'monthlyTasks')"
						@keydown.backspace="handleBulletedTextareaBackspace($event, form.content.scope, 'monthlyTasks')"
						@keydown.delete="handleBulletedTextareaDelete($event, form.content.scope, 'monthlyTasks')"
					></textarea>
					<PresetTaskListManager
						ref="assignmentMonthlyTasksManagerRef"
						:options="assignmentMonthlyTasksTemplate"
						title="Manage monthly tasks catalog"
						description=""
						:show-help="false"
						textarea-label="Monthly tasks catalog"
						close-label="Close monthly tasks catalog"
						:save-options="saveAssignmentMonthlyTasksTemplate"
						save-error-message="Could not save the monthly tasks catalog. Please try again."
					/>
					<label v-if="form.content.scope.monthlyTasksEnabled" class="form-group assignment-wide-field">
						<span>Monthly footnote</span>
						<input v-model="form.content.scope.monthlyFootnote" type="text" class="field-input" />
					</label>
				</section>

				<section id="assignment-sec-schedule" class="editor-section">
					<div class="section-heading">
						<h2>Cleaning schedule</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add schedule row" aria-label="Add schedule row" @click="addAssignmentScheduleRow">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add row</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'schedule'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('schedule')" :aria-label="sectionImportTitle('schedule')" @click="openSectionImport('schedule', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll assignment-schedule-scroll">
						<table class="editor-table assignment-schedule-table row-format-table">
							<thead>
								<tr>
									<th>Label</th>
									<th>Value</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(row, index) in form.content.schedule.rows" :key="`assignment-schedule-${index}`">
									<td class="row-format-cell">
										<RowFormatBar
											v-if="scheduleFormatRow === row"
											:row="row"
											:label="`Format schedule row ${index + 1}`"
											@toggle="(style) => toggleScheduleRowStyle(row, style)"
											@color="(color) => setScheduleRowColor(row, color)"
										/>
										<input
											:aria-label="`Schedule row ${index + 1} label`"
											v-model="row.label"
											type="text"
											class="table-input"
											:class="scheduleRowFormatClass(row)"
											@focus="openScheduleRowFormat(row)"
											@blur="closeScheduleRowFormatOnBlur"
										/>
									</td>
									<td>
										<input
											:aria-label="`Schedule row ${index + 1} value`"
											v-model="row.value"
											type="text"
											class="table-input"
											:class="scheduleRowFormatClass(row)"
											@focus="openScheduleRowFormat(row)"
											@blur="closeScheduleRowFormatOnBlur"
										/>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove schedule row" aria-label="Remove schedule row" @click="removeAt(form.content.schedule.rows, index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section id="assignment-sec-pricing" class="editor-section">
					<div class="section-heading">
						<h2>Pricing</h2>
					</div>
					<div class="pricing-grid assignment-pricing-grid">
						<div class="assignment-pricing-inputs">
							<div class="pricing-field-grid editor-details-grid">
								<label class="form-group">
									<span>Hourly rate (£)</span>
									<input v-model.number="form.content.pricing.hourlyRate" type="number" step="0.01" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>Hours per visit</span>
									<input v-model.number="form.content.pricing.hoursPerVisit" type="number" step="0.25" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>Visits per week</span>
									<input v-model.number="form.content.pricing.visitsPerWeek" type="number" step="1" min="0" class="field-input" />
								</label>
							</div>
						</div>
						<div class="pricing-card pricing-card--summary">
							<dl class="pricing-stats">
								<div class="pricing-stat">
									<dt>Fee per visit</dt>
									<dd>{{ formatCurrency(assignmentTotals.feePerVisit) }}</dd>
								</div>
								<div class="pricing-stat">
									<dt>Total weekly hours</dt>
									<dd>{{ assignmentTotals.totalWeeklyHours }}</dd>
								</div>
								<div class="pricing-stat pricing-stat--total">
									<dt>Weekly cost</dt>
									<dd>{{ formatCurrency(assignmentTotals.weeklyCost) }}</dd>
								</div>
							</dl>
						</div>
					</div>

					<details class="pricing-overrides">
						<summary>Manual overrides</summary>
						<div class="pricing-overrides__body">
							<p class="pricing-overrides__hint">Leave blank to use the calculated values and automatically generated document text. Placeholders show the current values.</p>
							<div class="pricing-field-grid">
								<label class="form-group pricing-field-grid__wide">
									<span>Fee per visit document text</span>
									<input
										id="assignment-fee-per-visit-text-override"
										v-model="form.content.pricing.overrides.feePerVisitText"
										type="text"
										class="field-input"
										:placeholder="assignmentFeePerVisitGeneratedText"
									/>
									<small class="pricing-overrides__field-hint">Replaces the complete Fee per Visit value shown in preview and PDF.</small>
								</label>
								<label class="form-group">
									<span>Fee per visit</span>
									<input
										id="assignment-fee-per-visit-override"
										v-model="form.content.pricing.overrides.feePerVisit"
										type="number"
										step="0.01"
										min="0"
										inputmode="decimal"
										class="field-input"
										:placeholder="formatCurrency(assignmentTotals.feePerVisit)"
									/>
								</label>
								<label class="form-group">
									<span>Weekly cost</span>
									<input v-model="form.content.pricing.overrides.weeklyCost" type="number" step="0.01" class="field-input" :placeholder="formatCurrency(assignmentTotals.weeklyCost)" />
								</label>
							</div>
						</div>
					</details>

					<div class="pricing-notes-group">
						<div class="pricing-notes-group__heading">
							<label for="assignment-pricing-notes">Pricing notes</label>
							<button
								ref="assignmentPricingNotesManagerButtonRef"
								class="icon-btn icon-btn--add"
								type="button"
								title="Manage pricing notes catalog"
								aria-label="Manage pricing notes catalog"
								@click="openAssignmentPricingNotesManager"
							>
								<AppointmentIcon name="edit" :size="18" />
								<span class="action-label">Manage catalog</span>
							</button>
							<button v-if="sectionImportUndo?.sectionId === 'pricing-notes'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
								<AppointmentIcon name="undo" :size="18" />
								<span class="action-label">Undo import</span>
							</button>
							<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('pricing-notes')" :aria-label="sectionImportTitle('pricing-notes')" @click="openSectionImport('pricing-notes', $event)">
								<AppointmentIcon name="assign" :size="18" />
								<span class="action-label">Import</span>
							</button>
						</div>
						<textarea
							id="assignment-pricing-notes"
							v-textarea-autosize
							:value="listToBulletedText(form.content.pricing.notes)"
							rows="1"
							class="table-textarea bulleted-textarea compact-textarea pricing-notes-textarea"
							@input="updateBulletedStringList(form.content.pricing, 'notes', $event.target.value, $event.target)"
							@keydown.enter="handleBulletedTextareaEnter($event, form.content.pricing, 'notes')"
							@keydown.backspace="handleBulletedTextareaBackspace($event, form.content.pricing, 'notes')"
							@keydown.delete="handleBulletedTextareaDelete($event, form.content.pricing, 'notes')"
						></textarea>
						<PresetTaskListManager
							ref="assignmentPricingNotesManagerRef"
							:options="assignmentPricingNotesTemplate"
							title="Manage pricing notes catalog"
							description=""
							:show-help="false"
							textarea-label="Pricing notes catalog"
							close-label="Close pricing notes catalog"
							:save-options="saveAssignmentPricingNotesTemplate"
							save-error-message="Could not save the pricing notes catalog. Please try again."
						/>
					</div>
				</section>

				<section id="assignment-sec-site" class="editor-section">
					<div class="section-heading">
						<h2>Site information</h2>
						<button v-if="sectionImportUndo?.sectionId === 'site'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('site')" :aria-label="sectionImportTitle('site')" @click="openSectionImport('site', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						aria-label="Site information"
						:value="listToText(form.content.siteInformation.paragraphs)"
						rows="1"
						class="table-textarea assignment-wide-textarea"
						@input="updateStringList(form.content.siteInformation, 'paragraphs', $event.target.value)"
					></textarea>
				</section>

				<section id="assignment-sec-terms" class="editor-section">
					<div class="section-heading">
						<h2>Terms</h2>
						<button v-if="sectionImportUndo?.sectionId === 'terms'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('terms')" :aria-label="sectionImportTitle('terms')" @click="openSectionImport('terms', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						aria-label="Terms"
						:value="listToText(form.content.terms.paragraphs)"
						rows="1"
						class="table-textarea assignment-wide-textarea"
						@input="updateStringList(form.content.terms, 'paragraphs', $event.target.value)"
					></textarea>
				</section>

				<section id="assignment-sec-contact" class="editor-section">
					<div class="section-heading">
						<h2>Contact</h2>
						<button v-if="sectionImportUndo?.sectionId === 'contact'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('contact')" :aria-label="sectionImportTitle('contact')" @click="openSectionImport('contact', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						aria-label="Contact"
						:value="listToText(form.content.contact.paragraphs)"
						rows="1"
						class="table-textarea assignment-wide-textarea"
						@input="updateStringList(form.content.contact, 'paragraphs', $event.target.value)"
					></textarea>
				</section>

				<section id="assignment-sec-acceptance" class="editor-section">
					<div class="section-heading">
						<h2>Acceptance</h2>
						<button v-if="sectionImportUndo?.sectionId === 'acceptance'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('acceptance')" :aria-label="sectionImportTitle('acceptance')" @click="openSectionImport('acceptance', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<label class="form-group assignment-wide-field">
						<span>Intro</span>
						<input v-model="form.content.acceptance.intro" type="text" class="field-input" />
					</label>
					<textarea
						v-textarea-autosize
						aria-label="Acceptance checklist"
						:value="listToBulletedText(form.content.acceptance.checklist)"
						rows="1"
						class="table-textarea bulleted-textarea assignment-wide-textarea"
						@input="updateBulletedStringList(form.content.acceptance, 'checklist', $event.target.value, $event.target)"
						@keydown.enter="handleBulletedTextareaEnter($event, form.content.acceptance, 'checklist')"
						@keydown.backspace="handleBulletedTextareaBackspace($event, form.content.acceptance, 'checklist')"
						@keydown.delete="handleBulletedTextareaDelete($event, form.content.acceptance, 'checklist')"
					></textarea>
				</section>

				<section id="assignment-sec-signatures" class="editor-section">
					<div class="section-heading">
						<h2>Signatures</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add signatory" aria-label="Add signatory" @click="addAssignmentSignatory">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add signatory</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'signatures'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('signatures')" :aria-label="sectionImportTitle('signatures')" @click="openSectionImport('signatures', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table assignment-signatory-table">
							<thead>
								<tr>
									<th>Label</th>
									<th>Name</th>
									<th>Role</th>
									<th>Mobile</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(signatory, index) in form.content.acceptance.signatories" :key="`assignment-signatory-${index}`">
									<td><input :aria-label="`Signatory ${index + 1} label`" v-model="signatory.label" type="text" class="table-input" /></td>
									<td><input :aria-label="`Signatory ${index + 1} name`" v-model="signatory.name" type="text" class="table-input" /></td>
									<td><input :aria-label="`Signatory ${index + 1} role`" v-model="signatory.role" type="text" class="table-input" /></td>
									<td><input :aria-label="`Signatory ${index + 1} mobile`" v-model="signatory.mobile" type="text" class="table-input" /></td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove signatory" aria-label="Remove signatory" @click="removeAt(form.content.acceptance.signatories, index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section id="assignment-sec-footer" class="editor-section">
					<div class="section-heading">
						<h2>Document footer</h2>
						<button v-if="sectionImportUndo?.sectionId === 'footer'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('footer')" :aria-label="sectionImportTitle('footer')" @click="openSectionImport('footer', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						aria-label="Document footer text"
						v-model="form.content.footerText"
						rows="1"
						class="table-textarea compact-textarea document-footer-textarea"
					></textarea>
				</section>
				<div class="toolbar-bottom-spacer" aria-hidden="true"></div>
			</form>

			<form v-else class="proposal-form editor-column-form" autocomplete="off" @submit.prevent="handleSave">
				<nav class="editor-outline" aria-label="Document sections">
					<p class="editor-outline__title">On this page</p>
					<button
						v-for="section in outlineSections"
						:key="section.id"
						type="button"
						class="editor-outline__link"
						:class="{ 'is-active': activeOutlineSectionId === section.id }"
						@click="scrollToOutlineSection(section.id)"
					>
						{{ section.label }}
					</button>
				</nav>
				<section id="proposal-sec-summary" class="editor-section">
					<div class="section-heading">
						<h2>Proposal summary</h2>
						<button v-if="sectionImportUndo?.sectionId === 'summary'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('summary')" :aria-label="sectionImportTitle('summary')" @click="openSectionImport('summary', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<textarea
						v-textarea-autosize
						aria-label="Proposal summary"
						:value="listToText(form.content.summaryParagraphs)"
						rows="1"
						class="table-textarea proposal-summary-textarea"
						@input="updateStringList(form.content, 'summaryParagraphs', $event.target.value)"
					></textarea>
				</section>

				<section id="proposal-sec-areas" class="editor-section">
					<div class="section-heading">
						<h2>Service areas</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add area" aria-label="Add area" @click="addServiceArea">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add area</span>
						</button>
						<button
							ref="proposalServiceAreaManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage service area bullet catalog"
							aria-label="Manage service area bullet catalog"
							@click="openProposalServiceAreaManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Manage catalog</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'areas'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('areas')" :aria-label="sectionImportTitle('areas')" @click="openSectionImport('areas', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div v-if="form.content.serviceAreas.length" class="document-table-scroll">
						<table class="editor-table service-areas-table">
							<thead>
								<tr>
									<th>Area title</th>
									<th>Bullets</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(area, areaIndex) in form.content.serviceAreas" :key="`area-${areaIndex}`">
									<td><input :aria-label="`Service area ${areaIndex + 1} title`" v-model="area.title" type="text" class="table-input" /></td>
									<td>
										<textarea
											v-textarea-autosize
											:aria-label="`Service area ${areaIndex + 1} bullets`"
											:value="listToBulletedText(area.items)"
											rows="1"
											class="table-textarea bulleted-textarea"
											@input="updateBulletedStringList(area, 'items', $event.target.value, $event.target)"
											@keydown.enter="handleBulletedTextareaEnter($event, area, 'items')"
											@keydown.backspace="handleBulletedTextareaBackspace($event, area, 'items')"
											@keydown.delete="handleBulletedTextareaDelete($event, area, 'items')"
										></textarea>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove area" aria-label="Remove area" @click="removeAt(form.content.serviceAreas, areaIndex)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<p v-else class="proposal-properties-empty">No service areas added.</p>
					<PresetTaskListManager
						ref="proposalServiceAreaManagerRef"
						:options="proposalServiceAreaItemsTemplate"
						title="Manage service area bullet catalog"
						description=""
						:show-help="false"
						textarea-label="Service area bullet catalog"
						close-label="Close service area bullet catalog"
						:save-options="saveProposalServiceAreaItemsTemplate"
						save-error-message="Could not save the service area bullet catalog. Please try again."
					/>
				</section>

				<section id="proposal-sec-tasks" class="editor-section">
					<div class="section-heading">
						<h2>Tasks</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add task section" aria-label="Add task section" @click="addTaskSection">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add task section</span>
						</button>
						<button
							ref="proposalWeeklyTasksManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage weekly tasks catalog"
							aria-label="Manage proposal weekly tasks catalog"
							@click="openProposalWeeklyTasksManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Weekly catalog</span>
						</button>
						<button
							ref="proposalMonthlyTasksManagerButtonRef"
							class="icon-btn icon-btn--add"
							type="button"
							title="Manage monthly tasks catalog"
							aria-label="Manage proposal monthly tasks catalog"
							@click="openProposalMonthlyTasksManager"
						>
							<AppointmentIcon name="edit" :size="18" />
							<span class="action-label">Monthly catalog</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'tasks'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('tasks')" :aria-label="sectionImportTitle('tasks')" @click="openSectionImport('tasks', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div v-if="form.content.taskSections.length" class="document-table-scroll">
						<table class="editor-table tasks-table">
							<thead>
								<tr>
									<th>Section</th>
									<th>Items</th>
									<th>Footnote</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(section, sectionIndex) in form.content.taskSections" :key="`tasks-${sectionIndex}`">
									<td><input :aria-label="`Section ${sectionIndex + 1} title`" v-model="section.title" type="text" class="table-input" /></td>
									<td>
										<textarea
											v-textarea-autosize
											:aria-label="`Task section ${sectionIndex + 1} items`"
											:value="listToBulletedText(section.items)"
											rows="1"
											class="table-textarea bulleted-textarea"
											@input="updateBulletedStringList(section, 'items', $event.target.value, $event.target)"
											@keydown.enter="handleBulletedTextareaEnter($event, section, 'items')"
											@keydown.backspace="handleBulletedTextareaBackspace($event, section, 'items')"
											@keydown.delete="handleBulletedTextareaDelete($event, section, 'items')"
										></textarea>
									</td>
									<td
										class="task-footnote-cell"
										v-tooltip="() => section.footnote"
										@click.self="$event.currentTarget.querySelector('textarea')?.focus({ preventScroll: true })"
									>
										<textarea v-textarea-autosize :aria-label="`Task section ${sectionIndex + 1} footnote`" v-model="section.footnote" rows="1" class="table-textarea compact-textarea"></textarea>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove task section" aria-label="Remove task section" @click="removeAt(form.content.taskSections, sectionIndex)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<p v-else class="proposal-properties-empty">No task sections added.</p>
					<PresetTaskListManager
						ref="proposalWeeklyTasksManagerRef"
						:options="proposalWeeklyTasksTemplate"
						title="Manage weekly tasks catalog"
						description=""
						:show-help="false"
						textarea-label="Weekly tasks catalog"
						close-label="Close weekly tasks catalog"
						:save-options="saveProposalWeeklyTasksTemplate"
						save-error-message="Could not save the weekly tasks catalog. Please try again."
					/>
					<PresetTaskListManager
						ref="proposalMonthlyTasksManagerRef"
						:options="proposalMonthlyTasksTemplate"
						title="Manage monthly tasks catalog"
						description=""
						:show-help="false"
						textarea-label="Monthly tasks catalog"
						close-label="Close monthly tasks catalog"
						:save-options="saveProposalMonthlyTasksTemplate"
						save-error-message="Could not save the monthly tasks catalog. Please try again."
					/>
				</section>

				<section id="proposal-sec-scope" class="editor-section">
					<div class="section-heading">
						<h2>Scope clarification</h2>
						<button v-if="sectionImportUndo?.sectionId === 'scope'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('scope')" :aria-label="sectionImportTitle('scope')" @click="openSectionImport('scope', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table scope-clarification-table">
							<thead>
								<tr>
									<th>Section title</th>
									<th>Paragraphs</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr>
									<td><input v-model="form.content.scopeClarification.title" type="text" class="table-input" aria-label="Scope clarification title" /></td>
									<td>
										<textarea
											v-textarea-autosize
											:value="listToText(form.content.scopeClarification.paragraphs)"
											rows="1"
											class="table-textarea"
											aria-label="Scope clarification paragraphs"
											@input="updateStringList(form.content.scopeClarification, 'paragraphs', $event.target.value)"
										></textarea>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Clear scope clarification text" aria-label="Clear scope clarification text" @click="form.content.scopeClarification.paragraphs = []">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section id="proposal-sec-schedule" class="editor-section">
					<div class="section-heading">
						<h2>Cleaning schedule</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add schedule row" aria-label="Add schedule row" @click="addScheduleRow">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add row</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'schedule'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('schedule')" :aria-label="sectionImportTitle('schedule')" @click="openSectionImport('schedule', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table schedule-table row-format-table">
							<tbody>
									<tr>
										<td class="move-col move-col--empty"></td>
										<td colspan="2">
											<textarea v-textarea-autosize aria-label="Cleaning schedule introduction" v-model="form.content.schedule.intro" rows="1" class="table-textarea compact-textarea"></textarea>
										</td>
										<td class="action-col"></td>
									</tr>
								<tr
									v-for="(row, index) in form.content.schedule.rows"
									:key="`schedule-${index}`"
									class="proposal-reorder-row"
									:class="{ 'is-row-dragging': isProposalRowDragging('schedule', index) }"
									data-proposal-reorder-group="schedule"
									:data-proposal-reorder-index="index"
								>
									<td class="move-col">
										<button
											class="icon-btn row-move-handle"
											type="button"
											title="Move schedule row (drag or use arrow keys)"
											:aria-label="`Move schedule row ${index + 1}`"
											aria-keyshortcuts="ArrowUp ArrowDown"
											@pointerdown="startProposalRowDrag($event, 'schedule', index)"
											@keydown.up.prevent="moveProposalRowBy('schedule', index, -1)"
											@keydown.down.prevent="moveProposalRowBy('schedule', index, 1)"
										>
											<BsGripVertical class="action-icon" />
										</button>
									</td>
									<td class="row-format-cell">
										<RowFormatBar
											v-if="scheduleFormatRow === row"
											:row="row"
											:label="`Format schedule row ${index + 1}`"
											block-toggle
											@toggle="(style) => onScheduleRowToggle(row, style)"
											@color="(color) => setScheduleRowColor(row, color)"
										/>
										<input
											:aria-label="`Schedule row ${index + 1} label`"
											v-model="row.label"
											type="text"
											class="table-input"
											:class="[scheduleRowFormatClass(row), { 'is-block-row': row.block || row.total }]"
											@focus="openScheduleRowFormat(row)"
											@blur="closeScheduleRowFormatOnBlur"
										/>
										<span v-if="row.block" class="schedule-block-chip">Block</span>
										<span v-else-if="row.total" class="schedule-block-chip">Total</span>
									</td>
									<td>
										<input
											:aria-label="`Schedule row ${index + 1} value`"
											v-model="row.value"
											type="text"
											class="table-input"
											:class="[scheduleRowFormatClass(row), { 'is-total-value': row.total }]"
											:readonly="row.total"
											:title="row.total ? 'The hours of the block rows, added up' : undefined"
											:placeholder="row.total ? 'Mark rows as Block to add up their hours' : undefined"
											@focus="openScheduleRowFormat(row)"
											@blur="closeScheduleRowFormatOnBlur"
										/>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove schedule row" aria-label="Remove schedule row" @click="removeAt(form.content.schedule.rows, index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section id="proposal-sec-pricing" class="editor-section">
					<div class="section-heading">
						<h2>Pricing</h2>
					</div>

					<div class="pricing-grid proposal-pricing-grid">
						<div v-if="pricingBlockRows.length" class="proposal-pricing-inputs proposal-block-pricing">
							<table class="editor-table pricing-blocks-table">
								<thead>
									<tr>
										<th>Block</th>
										<th class="numeric">Hours / week</th>
										<th>Hourly rate (£)</th>
										<th class="numeric">Weekly cost</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="(block, index) in pricingBlockRows" :key="`pricing-block-${index}`">
										<td class="pricing-block-cell">{{ block.name || 'Unnamed block' }}</td>
										<td class="pricing-block-cell numeric">{{ block.hoursPerWeek }}</td>
										<td>
											<input
												v-model.number="block.row.hourlyRate"
												:aria-label="`Hourly rate for ${block.name || `block ${index + 1}`}`"
												type="number"
												step="0.01"
												min="0"
												class="table-input"
											/>
										</td>
										<td class="pricing-block-cell numeric">{{ formatCurrency(block.weeklyCost) }}</td>
									</tr>
								</tbody>
							</table>
							<p class="pricing-blocks-hint">Names and hours come from the Cleaning schedule rows marked as Block.</p>
							<div class="pricing-card">
								<div class="pricing-field-grid">
									<label class="form-group">
										<span>Weeks / year</span>
										<input v-model.number="form.content.pricing.weeksPerYear" type="number" step="1" min="0" class="field-input" />
									</label>
									<label class="form-group">
										<span>VAT rate</span>
										<input v-model.number="form.content.pricing.vatRate" type="number" step="0.01" min="0" class="field-input" />
									</label>
								</div>
							</div>
						</div>
						<div v-else class="pricing-card proposal-pricing-inputs">
							<div class="pricing-field-grid">
								<label class="form-group">
									<span>Hourly rate (£)</span>
									<input v-model.number="form.content.pricing.hourlyRate" type="number" step="0.01" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>Hours per visit</span>
									<input v-model.number="form.content.pricing.hoursPerVisit" type="number" step="0.25" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>{{ pricingTotals.blockCount === 1 ? 'Visits / week' : 'Visits / week / block' }}</span>
									<input v-model.number="form.content.pricing.visitsPerWeekPerBlock" type="number" step="1" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>Block count</span>
									<input v-model.number="form.content.pricing.blockCount" type="number" step="1" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>Weeks / year</span>
									<input v-model.number="form.content.pricing.weeksPerYear" type="number" step="1" min="0" class="field-input" />
								</label>
								<label class="form-group">
									<span>VAT rate</span>
									<input v-model.number="form.content.pricing.vatRate" type="number" step="0.01" min="0" class="field-input" />
								</label>
							</div>
						</div>

						<div class="pricing-card pricing-card--summary">
							<dl class="pricing-stats">
								<div v-if="pricingTotals.usesBlocks" class="pricing-stat">
									<dt>Hours / week</dt>
									<dd>{{ pricingTotals.hoursPerWeek }}</dd>
								</div>
								<div v-else class="pricing-stat">
									<dt>{{ pricingTotals.blockCount === 1 ? 'Weekly cost' : 'Weekly cost / block' }}</dt>
									<dd>{{ formatCurrency(pricingTotals.weeklyCostPerBlock) }}</dd>
								</div>
								<div v-if="pricingTotals.usesBlocks || pricingTotals.blockCount !== 1" class="pricing-stat">
									<dt>Weekly (combined)</dt>
									<dd>{{ formatCurrency(pricingTotals.weeklyCombined) }}</dd>
								</div>
								<div class="pricing-stat">
									<dt>Annual excl. VAT</dt>
									<dd>{{ formatCurrency(pricingTotals.annualExVat) }}</dd>
								</div>
								<div class="pricing-stat">
									<dt>VAT</dt>
									<dd>{{ formatCurrency(pricingTotals.vat) }}</dd>
								</div>
								<div class="pricing-stat pricing-stat--total">
									<dt>Annual incl. VAT</dt>
									<dd>{{ formatCurrency(pricingTotals.annualIncVat) }}</dd>
								</div>
							</dl>
						</div>
					</div>

					<div class="pricing-notes-group">
						<div class="pricing-notes-group__heading">
							<label for="proposal-pricing-notes">Pricing notes</label>
							<button
								ref="proposalPricingNotesManagerButtonRef"
								class="icon-btn icon-btn--add"
								type="button"
								title="Manage proposal pricing notes catalog"
								aria-label="Manage proposal pricing notes catalog"
								@click="openProposalPricingNotesManager"
							>
								<AppointmentIcon name="edit" :size="18" />
								<span class="action-label">Manage catalog</span>
							</button>
							<button v-if="sectionImportUndo?.sectionId === 'pricing-notes'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
								<AppointmentIcon name="undo" :size="18" />
								<span class="action-label">Undo import</span>
							</button>
							<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('pricing-notes')" :aria-label="sectionImportTitle('pricing-notes')" @click="openSectionImport('pricing-notes', $event)">
								<AppointmentIcon name="assign" :size="18" />
								<span class="action-label">Import</span>
							</button>
						</div>
						<textarea
							id="proposal-pricing-notes"
							v-textarea-autosize
							:value="listToBulletedText(form.content.pricing.notes)"
							rows="1"
							class="table-textarea bulleted-textarea compact-textarea pricing-notes-textarea"
							@input="updateBulletedStringList(form.content.pricing, 'notes', $event.target.value, $event.target)"
							@keydown.enter="handleBulletedTextareaEnter($event, form.content.pricing, 'notes')"
							@keydown.backspace="handleBulletedTextareaBackspace($event, form.content.pricing, 'notes')"
							@keydown.delete="handleBulletedTextareaDelete($event, form.content.pricing, 'notes')"
						></textarea>
						<PresetTaskListManager
							ref="proposalPricingNotesManagerRef"
							:options="proposalPricingNotesTemplate"
							title="Manage proposal pricing notes catalog"
							description=""
							:show-help="false"
							textarea-label="Proposal pricing notes catalog"
							close-label="Close proposal pricing notes catalog"
							:save-options="saveProposalPricingNotesTemplate"
							save-error-message="Could not save the proposal pricing notes catalog. Please try again."
						/>
					</div>

				</section>

				<section id="proposal-sec-text" class="editor-section">
					<div class="section-heading">
						<h2>Text sections</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add section" aria-label="Add section" @click="addTextSection">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add section</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'text'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('text')" :aria-label="sectionImportTitle('text')" @click="openSectionImport('text', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table text-sections-table">
							<thead>
								<tr>
									<th class="move-col"></th>
									<th>Section title</th>
									<th>Paragraphs</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(section, sectionIndex) in form.content.textSections"
									:key="section.id || `text-${sectionIndex}`"
									class="proposal-reorder-row"
									:class="{ 'is-row-dragging': isProposalRowDragging('text-sections', sectionIndex) }"
									data-proposal-reorder-group="text-sections"
									:data-proposal-reorder-index="sectionIndex"
								>
									<td class="move-col">
										<button
											class="icon-btn row-move-handle"
											type="button"
											title="Move text section (drag or use arrow keys)"
											:aria-label="`Move text section ${sectionIndex + 1}`"
											aria-keyshortcuts="ArrowUp ArrowDown"
											@pointerdown="startProposalRowDrag($event, 'text-sections', sectionIndex)"
											@keydown.up.prevent="moveProposalRowBy('text-sections', sectionIndex, -1)"
											@keydown.down.prevent="moveProposalRowBy('text-sections', sectionIndex, 1)"
										>
											<BsGripVertical class="action-icon" />
										</button>
									</td>
									<td><input :aria-label="`Section ${sectionIndex + 1} title`" v-model="section.title" type="text" class="table-input" /></td>
									<td>
										<textarea
											v-textarea-autosize
											:aria-label="`Text section ${sectionIndex + 1} paragraphs`"
											:value="listToText(section.paragraphs)"
											rows="1"
											class="table-textarea"
											@input="updateStringList(section, 'paragraphs', $event.target.value)"
										></textarea>
									</td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove section" aria-label="Remove section" @click="removeAt(form.content.textSections, sectionIndex)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</section>

				<section id="proposal-sec-acceptance" class="editor-section">
					<div class="section-heading">
						<h2>Acceptance</h2>
						<button class="icon-btn icon-btn--add" type="button" title="Add signatory" aria-label="Add signatory" @click="addSignatory">
							<AppointmentIcon name="plus" :size="18" />
							<span class="action-label">Add signatory</span>
						</button>
						<button v-if="sectionImportUndo?.sectionId === 'acceptance'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
							<AppointmentIcon name="undo" :size="18" />
							<span class="action-label">Undo import</span>
						</button>
						<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('acceptance')" :aria-label="sectionImportTitle('acceptance')" @click="openSectionImport('acceptance', $event)">
							<AppointmentIcon name="assign" :size="18" />
							<span class="action-label">Import</span>
						</button>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table acceptance-table">
							<tbody>
								<tr>
									<td>
										<textarea
											v-textarea-autosize
											aria-label="Acceptance text"
											:value="listToText(form.content.acceptance.paragraphs)"
											rows="1"
											class="table-textarea compact-textarea acceptance-textarea"
											@input="updateStringList(form.content.acceptance, 'paragraphs', $event.target.value)"
										></textarea>
									</td>
								</tr>
								<tr>
									<td><input aria-label="Signature label" v-model="form.content.acceptance.signatureLabel" type="text" class="table-input" /></td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="document-table-scroll">
						<table class="editor-table signatory-table">
							<thead>
								<tr>
									<th>Label</th>
									<th>Name</th>
									<th>Role</th>
									<th class="action-col"></th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(signatory, index) in form.content.acceptance.signatories" :key="`signatory-${index}`">
									<td><input :aria-label="`Signatory ${index + 1} label`" v-model="signatory.label" type="text" class="table-input" /></td>
									<td><input :aria-label="`Signatory ${index + 1} name`" v-model="signatory.name" type="text" class="table-input" /></td>
									<td><textarea v-textarea-autosize :aria-label="`Signatory ${index + 1} role`" v-model="signatory.role" rows="1" class="table-textarea compact-textarea signatory-role-textarea"></textarea></td>
									<td class="action-col">
										<button class="icon-btn icon-btn--danger" type="button" title="Remove signatory" aria-label="Remove signatory" @click="removeAt(form.content.acceptance.signatories, index)">
											<AppointmentIcon name="trash" :size="18" />
										</button>
									</td>
								</tr>
								</tbody>
						</table>
					</div>
					</section>

					<section id="proposal-sec-footer" class="editor-section">
						<div class="section-heading">
							<h2>Document footer</h2>
							<button v-if="sectionImportUndo?.sectionId === 'footer'" class="icon-btn icon-btn--add section-import-btn" type="button" title="Undo the import" aria-label="Undo the import" @click="undoSectionImport">
								<AppointmentIcon name="undo" :size="18" />
								<span class="action-label">Undo import</span>
							</button>
							<button class="icon-btn icon-btn--add section-import-btn" type="button" :title="sectionImportTitle('footer')" :aria-label="sectionImportTitle('footer')" @click="openSectionImport('footer', $event)">
								<AppointmentIcon name="assign" :size="18" />
								<span class="action-label">Import</span>
							</button>
						</div>
						<textarea
							v-textarea-autosize
							aria-label="Document footer text"
							v-model="form.content.footerText"
							rows="2"
							class="table-textarea compact-textarea document-footer-textarea"
						></textarea>
					</section>

					<div class="toolbar-bottom-spacer" aria-hidden="true"></div>
				</form>
			</template>

		<template #footer>
			<button class="floating-action-btn" type="button" @click="openPreview" :disabled="saving">
				<AppointmentIcon name="preview" :size="18" />
				<span>{{ `Preview ${documentLowerLabel}` }}</span>
			</button>
			<button class="floating-action-btn" type="button" @click="closePreview" v-if="showPreview">
				<AppointmentIcon name="close" :size="18" />
				<span>Close</span>
			</button>
			<button class="floating-action-btn" type="button" :disabled="saving || pdfGenerating" @click="downloadPdf">
				<AppointmentIcon name="download" :size="18" />
				<span>{{ pdfGenerating ? 'Generating...' : 'Download PDF' }}</span>
			</button>
			<button
				class="floating-action-btn"
				type="button"
				:disabled="documentCheck.status === 'checking'"
				@click="runDocumentCheck"
			>
				<AppointmentIcon name="proofread" :size="18" />
				<span>{{ documentCheck.status === 'checking' ? 'Checking...' : 'Check document' }}</span>
			</button>
			<button v-if="isSupplierDocument" class="floating-action-btn floating-action-btn--primary" type="button" :disabled="!canSaveDocument" @click="handleSave">
				<AppointmentIcon name="save" :size="18" />
				<span>{{ saving ? 'Saving...' : 'Save document' }}</span>
			</button>
			<button
				v-if="saveStatus?.retry"
				class="unsaved-pill unsaved-pill--error unsaved-pill--retry"
				type="button"
				:title="saveStatus.title || undefined"
				@click="saveClientDocumentNow"
			>{{ saveStatus.label }}</button>
			<span
				v-else-if="saveStatus"
				class="unsaved-pill"
				:class="`unsaved-pill--${saveStatus.tone}`"
				:title="saveStatus.title || undefined"
				role="status"
			>{{ saveStatus.label }}</span>
			<div v-if="errorMessage" class="error-inline">{{ errorMessage }}</div>
		</template>
	</InvoiceCreatorLayout>
	<div v-else class="loading-state">Loading document...</div>

	<InvoicePreview
		:show-preview="showPreview"
		:invoice-html="previewHtml"
		render-html-as-pdf
		:pdf-filename="previewPdfFilename"
		@close-preview="closePreview"
		@download-pdf="downloadPdf"
	/>

	<div v-if="documentCheck.status === 'checking'" class="document-check-scan" aria-hidden="true">
		<span class="document-check-scan-line"></span>
	</div>
	<DocumentCheckPanel
		v-if="documentCheck.open"
		:status="documentCheck.status"
		:findings="documentCheck.findings"
		:fields-checked="documentCheck.fieldsChecked"
		:error="documentCheck.error"
		:content="form.content"
		@fix="fixDocumentFinding"
		@skip="(finding) => (finding.state = 'skipped')"
		@fix-all="fixAllDocumentFindings"
		@retry="runDocumentCheck"
		@close="documentCheck.open = false"
	/>
	<DocumentConflictDialog
		v-if="autosave.state.status === 'conflict'"
		:document-label="documentLowerLabel"
		:busy="conflict.busy"
		:error="conflict.error"
		@load-latest="loadLatestAfterConflict"
		@keep-mine="keepMineAfterConflict"
	/>
	<SectionImportDialog
		v-if="sectionImport.open && sectionImportSection"
		:section-label="sectionImportSection.label"
		:document-label="documentLowerLabel"
		:sources="sectionImportSources"
		:loading="sectionImport.loading"
		:error="sectionImport.error"
		:return-focus="sectionImport.returnFocus"
		@import="importSectionFrom"
		@retry="refreshSectionImportDocuments"
		@close="closeSectionImport"
	/>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, onUnmounted, reactive, ref, toRaw, watch } from 'vue';
import { BsGripVertical } from '@kalimahapps/vue-icons';
import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import InvoicePreview from '@/components/invoices/InvoicePreview.vue';
import RowFormatBar from '@/components/documents/RowFormatBar.vue';
import DocumentCheckPanel from '@/components/documents/DocumentCheckPanel.vue';
import SectionImportDialog from '@/components/documents/SectionImportDialog.vue';
import DocumentConflictDialog from '@/components/documents/DocumentConflictDialog.vue';
import { useAutosave } from '@/composables/useAutosave';
import { useLiveRefresh } from '@/composables/useLiveRefresh';
import { applyFinding } from '@/utils/documentCheck';
import {
	buildSectionImportSources,
	getImportableSection,
	importSection,
	toSectionImportDocument,
	writeSectionValues,
} from '@/utils/clientDocumentSectionImport';
import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
import PresetTextInput from '@/components/documents/PresetTextInput.vue';
import PresetTextCombobox from '@/components/documents/PresetTextCombobox.vue';
import PresetTaskListManager from '@/components/documents/PresetTaskListManager.vue';
import PresetValuesManager from '@/components/documents/PresetValuesManager.vue';
import InvoiceCreatorLayout from '@/views/invoices/components/InvoiceCreatorLayout.vue';
import { scheduleTextareaResize, vTextareaAutosize } from '@/directives/textareaAutosize';
import vTooltip from '@/directives/tooltip';
import { useCatalogStore } from '@/stores/catalog';
import { useClientDocumentsStore } from '@/stores/clientDocuments';
import { useSupplierPortalAdminStore } from '@/stores/supplierPortalAdmin';
import { useMediaStore } from '@/stores/media';
import { useProjectsStore } from '@/stores/projects';
import { useTabsStore } from '@/stores/tabs';
import { convertImageToDataUrl } from '@/utils/imageUtils';
import { buildBusinessInfo } from '@/utils/invoiceDocument';
import { buildLegacyClientDocumentFooterText } from '@/utils/clientDocumentTheme';
import {
	buildProposalFooterText,
	calculateProposalPricing,
	createProposalTemplateContent,
	downloadProposalPdfFromHtml,
	generateClientDocumentPdfFileFromHtml,
	formatGbpAmount,
	getProposalPricingBlocks,
	getScheduleBlockName,
	getScheduleTotalValue,
	parseScheduleHours,
	PROPOSAL_DOCUMENT_TYPE,
	toNumberOrNull,
} from '@/utils/proposalDocument';
import {
	QUOTATION_DOCUMENT_TYPE,
	calculateQuotationTotals,
} from '@/utils/quotationDocument';
import {
	MAX_QUOTATION_PROPERTY_LENGTH,
	MAX_QUOTATION_SITE_ADDRESS_LENGTH,
	getQuotationCleaningStyleOptions,
	getQuotationCostItemOptions,
	getQuotationPropertyOptions,
	getQuotationSiteAddressOptions,
	mergeQuotationCleaningStyleOptions,
	mergeQuotationCostItemOptions,
	mergeQuotationPropertyOptions,
	mergeQuotationSiteAddressOptions,
} from '@/utils/quotationPresets';
import {
	calculateAssignmentConfirmationPricing,
	ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
	ASSIGNMENT_CONFIRMATION_TEMPLATE,
	DEFAULT_ASSIGNMENT_CONFIRMATION_SUBTITLE,
} from '@/utils/assignmentConfirmationDocument';
import {
	appendNewAssignmentMonthlyTasksFromCatalog,
	appendNewAssignmentPricingNotesFromCatalog,
	appendNewAssignmentRegularTasksFromCatalog,
	getAssignmentMonthlyTasksTemplate,
	getAssignmentPricingNotesTemplate,
	getAssignmentServiceAreaOptions,
	getAssignmentSubtitleOptions,
	getAssignmentRegularTasksTemplate,
	mergeAssignmentMonthlyTasksTemplate,
	mergeAssignmentPricingNotesTemplate,
	mergeAssignmentServiceAreaOptions,
	mergeAssignmentSubtitleOptions,
	mergeAssignmentRegularTasksTemplate,
} from '@/utils/assignmentSubtitlePresets';
import {
	createBulletedTextareaEditor,
	listToBulletedText,
} from '@/utils/bulletedTextarea';
import {
	DEFAULT_PROPOSAL_MONTHLY_TASKS_FOOTNOTE,
	applyProposalTaskTemplatesToNewContent,
	appendNewProposalTaskItemsFromCatalog,
	appendNewProposalServiceAreaItemsFromCatalog,
	getProposalMonthlyTasksTemplate,
	getProposalPricingNotesTemplate,
	getProposalServiceAreaItemsTemplate,
	getProposalWeeklyTasksTemplate,
	mergeProposalMonthlyTasksTemplate,
	mergeProposalPricingNotesTemplate,
	mergeProposalServiceAreaItemsTemplate,
	mergeProposalWeeklyTasksTemplate,
} from '@/utils/proposalPresets';
import {
	createClientDocumentContent,
	deriveClientDocumentFilename,
	generateClientDocumentHtml,
	getClientDocumentDefinition,
	getClientDocumentLabel,
	getClientDocumentLowerLabel,
	getClientDocumentReference,
	normalizeClientDocumentContent,
	resolveClientDocumentType,
} from '@/utils/clientDocumentRegistry';

const props = defineProps({
	documentId: {
		type: [String, Number],
		default: null,
	},
	draftId: {
		type: [String, Number],
		default: null,
	},
	documentType: {
		type: String,
		default: null,
	},
	initialContent: {
		type: Object,
		default: null,
	},
	tabId: {
		type: String,
		default: null,
	},
	documentDomain: {
		type: String,
		default: 'client',
		validator: (value) => ['client', 'supplier'].includes(value),
	},
});

const emit = defineEmits(['back-to-list']);

const clientDocumentsStore = useClientDocumentsStore();
const supplierPortalAdminStore = useSupplierPortalAdminStore();
const catalogStore = useCatalogStore();
const projectsStore = useProjectsStore();
const mediaStore = useMediaStore();
const tabsStore = useTabsStore();
const { proxy } = getCurrentInstance() || {};
const toast = proxy?.$toast;
const logError = proxy?.$logError;

const ready = ref(false);
const saving = ref(false);
const pdfGenerating = ref(false);
const showPreview = ref(false);
const errorMessage = ref('');
const logoUrl = ref('/logo-m.svg');
const documentRecord = ref(null);
const currentDocumentId = ref(props.documentId ? String(props.documentId) : null);
const pristineSnapshot = ref('');
const hasUnsavedChanges = ref(false);
// A document that could not be loaded is never saved over.
const loadFailed = ref(false);
// The last section import, kept so it can be undone: { sectionId, previous }.
const sectionImportUndo = ref(null);
const assignmentSubtitleTouched = ref(false);
const quotationCostItemManagerRef = ref(null);
const quotationCostItemManagerButtonRef = ref(null);
const assignmentServiceAreaInputRefs = ref([]);
const assignmentServiceAreaManagerRef = ref(null);
const assignmentServiceAreaManagerButtonRef = ref(null);
const assignmentServiceAreaAddButtonRef = ref(null);
const assignmentRegularTasksManagerRef = ref(null);
const assignmentRegularTasksManagerButtonRef = ref(null);
const assignmentMonthlyTasksManagerRef = ref(null);
const assignmentMonthlyTasksManagerButtonRef = ref(null);
const assignmentPricingNotesManagerRef = ref(null);
const assignmentPricingNotesManagerButtonRef = ref(null);
const proposalServiceAreaManagerRef = ref(null);
const proposalServiceAreaManagerButtonRef = ref(null);
const proposalWeeklyTasksManagerRef = ref(null);
const proposalWeeklyTasksManagerButtonRef = ref(null);
const proposalMonthlyTasksManagerRef = ref(null);
const proposalMonthlyTasksManagerButtonRef = ref(null);
const proposalPricingNotesManagerRef = ref(null);
const proposalPricingNotesManagerButtonRef = ref(null);
const proposalRowDrag = reactive({
	group: '',
	index: -1,
	pointerId: null,
	startX: 0,
	startY: 0,
	active: false,
});
let assignmentRegularTasksCatalogSnapshot = null;
let assignmentMonthlyTasksCatalogSnapshot = null;
let assignmentPricingNotesCatalogSnapshot = null;
let proposalServiceAreaItemsCatalogSnapshot = null;
let proposalWeeklyTasksCatalogSnapshot = null;
let proposalMonthlyTasksCatalogSnapshot = null;
let proposalPricingNotesCatalogSnapshot = null;

const isSupplierDocument = computed(() => props.documentDomain === 'supplier');

const form = reactive({
	content: normalizeClientDocumentContent(createProposalTemplateContent()),
});

const currentDocumentType = computed(() =>
	resolveClientDocumentType({
		documentType: form.content?.documentType || documentRecord.value?.documentType,
		content: form.content,
	}),
);

const isProposal = computed(() => currentDocumentType.value === PROPOSAL_DOCUMENT_TYPE);
const isQuotation = computed(
	() => currentDocumentType.value === QUOTATION_DOCUMENT_TYPE,
);
const isAssignmentConfirmation = computed(
	() => currentDocumentType.value === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE,
);
const businessInfoData = computed(() => buildBusinessInfo(projectsStore.activeProject));
const projectCompany = computed(() => {
	const settings = projectsStore.activeProject?.businessSettings || {};
	const current = form.content.company || {};
	return {
		name: settings.name || projectsStore.activeProject?.name || current.name || '',
		address: settings.address || current.address || '',
		companyNumber: settings.regNumber || settings.companyNumber || current.companyNumber || '',
		phone: settings.phone || current.phone || '',
		email: settings.email || current.email || '',
		website: settings.website || current.website || '',
	};
});
const businessName = computed(
	() => businessInfoData.value.businessName || projectCompany.value.name || '',
);
const proposalContentForOutput = computed(() =>
	normalizeClientDocumentContent({
		...form.content,
		company: projectCompany.value,
	}, currentDocumentType.value),
);

const pricingTotals = computed(() =>
	calculateProposalPricing(
		proposalContentForOutput.value.pricing,
		getProposalPricingBlocks(proposalContentForOutput.value),
	),
);
// Schedule rows marked as blocks, priced one by one: name and hours come from the row.
const pricingBlockRows = computed(() =>
	(form.content.schedule?.rows || [])
		.filter((row) => row.block)
		.map((row, index) => ({
			row,
			name: getScheduleBlockName(row.label),
			hoursPerWeek: parseScheduleHours(row.value),
			weeklyCost: pricingTotals.value.blocks[index]?.weeklyCost ?? 0,
		})),
);
const quotationTotals = computed(() => calculateQuotationTotals(proposalContentForOutput.value.costBreakdown));
const assignmentTotals = computed(() => calculateAssignmentConfirmationPricing(proposalContentForOutput.value.pricing));
const assignmentFeePerVisitGeneratedText = computed(() => {
	const totals = assignmentTotals.value;
	return `${formatGbpAmount(totals.feePerVisit)} (based on ${totals.hoursPerVisit} hours at ${formatGbpAmount(totals.hourlyRate)}/hr)`;
});
const previewHtml = computed(() =>
	generateClientDocumentHtml({
		content: proposalContentForOutput.value,
		logoUrl: logoUrl.value,
	}),
);
const previewPdfFilename = computed(() =>
	deriveClientDocumentFilename({
		documentType: currentDocumentType.value,
		content: proposalContentForOutput.value,
		reference: getClientDocumentReference({
			documentType: currentDocumentType.value,
			content: proposalContentForOutput.value,
		}),
	}),
);
const pageTitle = computed(() => {
	const label = getClientDocumentLowerLabel(currentDocumentType.value);
	const reference = getClientDocumentReference({
		documentType: currentDocumentType.value,
		content: proposalContentForOutput.value,
	});
	if (currentDocumentId.value) {
		return reference ? `Edit ${label} ${reference}` : `Edit ${label}`;
	}
	return `New ${label}`;
});
const documentLowerLabel = computed(() => getClientDocumentLowerLabel(currentDocumentType.value));
const canSaveDocument = computed(() => {
	if (saving.value) return false;
	if (!isSupplierDocument.value) return true;
	if (!documentRecord.value?.file?.id) return true;
	return hasUnsavedChanges.value;
});
const myTabId = computed(() => props.tabId || tabsStore.activeTabId || null);
const clientOptions = computed(() => catalogStore.clientOptions || []);
const supplierOptions = computed(() => catalogStore.supplierOptions || []);
const serviceOptions = computed(() => catalogStore.serviceOptions || []);
const buildingOptions = computed(() => catalogStore.buildingOptions || []);
const assignmentSubtitleOptions = computed(() =>
	getAssignmentSubtitleOptions(projectsStore.activeProject?.documentPresets),
);
const assignmentServiceAreaOptions = computed(() =>
	getAssignmentServiceAreaOptions(projectsStore.activeProject?.documentPresets),
);
const assignmentRegularTasksTemplate = computed(() =>
	getAssignmentRegularTasksTemplate(projectsStore.activeProject?.documentPresets),
);
const assignmentMonthlyTasksTemplate = computed(() =>
	getAssignmentMonthlyTasksTemplate(projectsStore.activeProject?.documentPresets),
);
const assignmentPricingNotesTemplate = computed(() =>
	getAssignmentPricingNotesTemplate(projectsStore.activeProject?.documentPresets),
);
const quotationCleaningStyleOptions = computed(() =>
	getQuotationCleaningStyleOptions(projectsStore.activeProject?.documentPresets),
);
const quotationPropertyOptions = computed(() =>
	getQuotationPropertyOptions(projectsStore.activeProject?.documentPresets),
);
const quotationSiteAddressOptions = computed(() =>
	getQuotationSiteAddressOptions(projectsStore.activeProject?.documentPresets),
);
const quotationCostItemOptions = computed(() =>
	getQuotationCostItemOptions(projectsStore.activeProject?.documentPresets),
);
const proposalServiceAreaItemsTemplate = computed(() =>
	getProposalServiceAreaItemsTemplate(projectsStore.activeProject?.documentPresets),
);
const proposalWeeklyTasksTemplate = computed(() =>
	getProposalWeeklyTasksTemplate(projectsStore.activeProject?.documentPresets),
);
const proposalMonthlyTasksTemplate = computed(() =>
	getProposalMonthlyTasksTemplate(projectsStore.activeProject?.documentPresets),
);
const proposalPricingNotesTemplate = computed(() =>
	getProposalPricingNotesTemplate(projectsStore.activeProject?.documentPresets),
);

const assignmentServiceAreaOptionsForIndex = (index) => {
	const rows = form.content?.scope?.serviceAreas || [];
	const currentKey = String(rows[index] ?? '').trim().toLocaleLowerCase();
	const usedKeys = new Set(
		rows
			.filter((_, rowIndex) => rowIndex !== index)
			.map((value) => String(value ?? '').trim().toLocaleLowerCase())
			.filter(Boolean),
	);
	return assignmentServiceAreaOptions.value.filter((option) => {
		const key = option.trim().toLocaleLowerCase();
		return key === currentKey || !usedKeys.has(key);
	});
};

const serializeSnapshot = () =>
	JSON.stringify({
		content: proposalContentForOutput.value,
	});

const markPristine = () => {
	pristineSnapshot.value = serializeSnapshot();
	hasUnsavedChanges.value = false;
	sectionImportUndo.value = null;
};

const syncFooterTextFromProject = () => {
	const projectFooter = buildProposalFooterText(projectCompany.value);
	const storedCompany = form.content.company || {};
	// Anything matching a generated footer (current or legacy format) is not a
	// manual edit and can safely be refreshed from the project settings.
	const generatedFooters = [
		buildProposalFooterText(storedCompany),
		buildLegacyClientDocumentFooterText(storedCompany),
		buildLegacyClientDocumentFooterText(projectCompany.value),
	];
	if (!form.content.footerText || generatedFooters.includes(form.content.footerText)) {
		form.content.footerText = projectFooter;
	}
};

const replaceContent = (
	content,
	{ syncProjectFooter = false, documentType = null } = {},
) => {
	const nextType =
		documentType ||
		content?.documentType ||
		documentRecord.value?.documentType ||
		form.content?.documentType;
	form.content = normalizeClientDocumentContent(content, nextType);
	if (syncProjectFooter) {
		syncFooterTextFromProject();
	}
};

const applyTabTitle = () => {
	if (!myTabId.value) return;
	const label = getClientDocumentLabel(currentDocumentType.value);
	const reference = getClientDocumentReference({
		documentType: currentDocumentType.value,
		content: proposalContentForOutput.value,
	});
	const updates = {
		title: reference ? `${label} ${reference}` : label,
		props: {
			documentId: currentDocumentId.value,
			draftId: currentDocumentId.value ? null : props.draftId,
			documentType: currentDocumentType.value,
			initialContent: currentDocumentId.value ? null : props.initialContent,
			documentDomain: props.documentDomain,
			tabId: myTabId.value,
		},
	};
	if (currentDocumentId.value) {
		updates.itemId = `${props.documentDomain}:${currentDocumentId.value}`;
		// A new document saved for the first time keeps its editor open as it is.
		const tab = tabsStore.tabs.find((entry) => entry.id === myTabId.value);
		if (tab && tab.cacheKey === undefined && tab.itemId !== updates.itemId) {
			updates.cacheKey = tab.itemId;
		}
	}
	tabsStore.updateTab(myTabId.value, updates);
};

const getLogoUrl = async () => {
	let resolvedLogoUrl = null;
	if (projectsStore.activeProject?.logo?.id) {
		try {
			const signed = await mediaStore.getSignedUrl(projectsStore.activeProject.logo.id);
			if (signed?.url) {
				resolvedLogoUrl = signed.url;
			}
		} catch (error) {
			logError?.(error, 'ClientDocumentEditorView getLogoUrl');
		}
	}
	if (!resolvedLogoUrl && projectsStore.activeProject?.logo?.url) {
		resolvedLogoUrl = projectsStore.activeProject.logo.url;
	}
	resolvedLogoUrl = resolvedLogoUrl || '/logo-m.svg';
	const dataUrl = await convertImageToDataUrl(resolvedLogoUrl);
	return dataUrl || resolvedLogoUrl;
};

const fetchSupplierDocument = async (documentId, { force = false } = {}) => {
	const findDocument = () =>
		(supplierPortalAdminStore.documents || []).find(
			(document) => String(document?.id) === String(documentId),
		) || null;
	if (typeof supplierPortalAdminStore.fetchDocument === 'function') {
		const document = await supplierPortalAdminStore.fetchDocument(documentId, { force });
		if (document) return document;
	}
	const cached = findDocument();
	if (cached) return cached;
	await supplierPortalAdminStore.fetchDocuments();
	return findDocument();
};

const proposalTaskTitleKey = (value) =>
	String(value ?? '').trim().toLocaleLowerCase();

const findProposalTaskSection = (sections, title) =>
	(Array.isArray(sections) ? sections : []).find(
		(section) => proposalTaskTitleKey(section?.title) === proposalTaskTitleKey(title),
	) || null;

const applyProposalTaskCatalogsToNewContent = (content) => {
	return applyProposalTaskTemplatesToNewContent(
		content,
		projectsStore.activeProject?.documentPresets,
	);
};

const loadDocument = async () => {
	errorMessage.value = '';
	loadFailed.value = false;
	try {
		if (!projectsStore.initialized) {
			await projectsStore.ensureInitialized();
		}
		logoUrl.value = await getLogoUrl();
		if (currentDocumentId.value) {
			// The latest saved version, not a list copy that may be older: saves build on it.
			const document = isSupplierDocument.value
				? await fetchSupplierDocument(currentDocumentId.value)
				: await clientDocumentsStore.fetchDocument(currentDocumentId.value, { forceRefresh: true });
			if (!document) {
				throw new Error('Document not found');
			}
			documentRecord.value = document;
			replaceContent(
				document?.content || createClientDocumentContent(
					isSupplierDocument.value
						? ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE
						: document?.documentType,
				),
				{
					syncProjectFooter: true,
					documentType: isSupplierDocument.value
						? ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE
						: document?.documentType,
				},
			);
			currentDocumentId.value = document?.documentId || document?.id || currentDocumentId.value;
			applyTabTitle();
		} else {
			const draftDocumentType =
				props.initialContent?.documentType ||
				props.documentType ||
				QUOTATION_DOCUMENT_TYPE;
			const draftContent = props.initialContent || createClientDocumentContent(draftDocumentType);
			replaceContent(
				!props.initialContent && draftDocumentType === PROPOSAL_DOCUMENT_TYPE
					? applyProposalTaskCatalogsToNewContent(draftContent)
					: draftContent,
				{
					syncProjectFooter: true,
					documentType: draftDocumentType,
				},
			);
			applyTabTitle();
		}
		markPristine();
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView loadDocument');
		errorMessage.value = error?.message || 'Failed to load document';
		loadFailed.value = true;
	} finally {
		ready.value = true;
	}
};

const buildSavePayload = () => {
	const definition = getClientDocumentDefinition(currentDocumentType.value);
	const content = isQuotation.value
		? {
				...proposalContentForOutput.value,
				serviceDetails: {
					...proposalContentForOutput.value.serviceDetails,
					serviceId: null,
					propertyId: null,
				},
			}
		: proposalContentForOutput.value;
	const metadata = {
		...(documentRecord.value?.metadata || {}),
		template: definition.template,
		sourceTemplate:
			documentRecord.value?.metadata?.sourceTemplate ||
			definition.sourceTemplate,
	};
	if (isSupplierDocument.value) {
		return {
			title: documentRecord.value?.title || definition.label,
			category: documentRecord.value?.category || 'assignments',
			sourceType: 'template',
			templateKey: definition.template,
			content,
			metadata,
		};
	}
	return {
		content,
		metadata,
	};
};

const saveSupplierTemplateDocument = async (savePayload) => {
	if (!isAssignmentConfirmation.value) {
		throw new Error('Only Assignment confirmation is supported for supplier templates');
	}
	const supplierId = proposalContentForOutput.value?.subcontractorId || null;
	if (!supplierId) {
		throw new Error('Select a supplier before publishing this assignment');
	}
	const filename = deriveClientDocumentFilename({
		documentType: currentDocumentType.value,
		content: proposalContentForOutput.value,
		reference: getClientDocumentReference({
			documentType: currentDocumentType.value,
			content: proposalContentForOutput.value,
		}),
	});
	const pdfFile = await generateClientDocumentPdfFileFromHtml({
		html: previewHtml.value,
		filename,
		logError,
	});
	const uploaded = await mediaStore.uploadFile(pdfFile);
	const fileId = uploaded?.id || null;
	if (!fileId) {
		throw new Error('Failed to upload generated PDF');
	}
	const completionMode =
		documentRecord.value?.completionMode === 'read_only'
			? 'read_only'
			: 'bilateral_signature';
	try {
		return await supplierPortalAdminStore.updateDocument(currentDocumentId.value, {
			...savePayload,
			fileId,
			supplierIds: [supplierId],
			completionMode,
			requiresEffectiveDate: completionMode === 'bilateral_signature',
		});
	} catch (error) {
		let refreshedDocument = null;
		let refreshSucceeded = false;
		try {
			refreshedDocument = await fetchSupplierDocument(currentDocumentId.value, { force: true });
			refreshSucceeded = true;
		} catch (refreshError) {
			logError?.(refreshError, 'ClientDocumentEditorView verifySupplierDocumentPublish');
		}
		if (String(refreshedDocument?.file?.id || '') === String(fileId)) {
			return refreshedDocument;
		}
		if (refreshSucceeded) {
			try {
				await mediaStore.deleteFile(fileId);
			} catch (cleanupError) {
				logError?.(cleanupError, 'ClientDocumentEditorView cleanupSupplierDocumentPdf');
			}
		}
		throw error;
	}
};

const isClientDocumentConflict = (error) =>
	error?.response?.data?.error?.details?.code === 'CLIENT_DOCUMENT_CHANGED';

// Saves the document as it is now without replacing what is on screen, so typing can go on while
// it saves: what was sent becomes the saved state, and anything typed meanwhile goes in the next
// save. The save names the revision it builds on, so it cannot overwrite changes made elsewhere.
const persistClientDocument = async () => {
	const payload = buildSavePayload();
	const snapshot = serializeSnapshot();
	if (documentRecord.value?.revision !== undefined) payload.revision = documentRecord.value.revision;
	const saved = await clientDocumentsStore.saveDocument({
		documentId: currentDocumentId.value,
		payload,
	});
	documentRecord.value = saved;
	currentDocumentId.value = saved?.documentId || saved?.id || currentDocumentId.value;
	pristineSnapshot.value = snapshot;
	hasUnsavedChanges.value = serializeSnapshot() !== snapshot;
	applyTabTitle();
};

// Client documents save on their own, a new one from its first change. A document published to a
// subcontractor does not: saving it sends them a new PDF, so that stays a press of Save.
const autosave = useAutosave({
	save: persistClientDocument,
	hasChanges: () => hasUnsavedChanges.value,
	isEnabled: () => ready.value && !loadFailed.value && !isSupplierDocument.value,
	isConflict: isClientDocumentConflict,
	isFatal: (error) => [400, 401, 403, 404].includes(error?.response?.status),
});

const formatSaveTime = (date) =>
	date ? date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

const saveFailureHint = (error) => {
	const status = error?.response?.status;
	if (status === 404) return 'This document no longer exists.';
	if (status === 401 || status === 403) return 'Sign in again to save.';
	if (status === 400) return error?.response?.data?.error?.message || 'The document could not be saved.';
	return 'There is no connection to the server. It tries again on its own.';
};

// When a supplier document was last saved here with Save.
const supplierSavedAt = ref(null);

const saveStatus = computed(() => {
	if (isSupplierDocument.value) {
		if (saving.value) return { label: 'Saving…', tone: 'saving' };
		if (hasUnsavedChanges.value) return { label: 'Not saved', tone: 'unsaved' };
		if (supplierSavedAt.value) {
			return { label: 'Saved', tone: 'saved', title: `Saved at ${formatSaveTime(supplierSavedAt.value)}` };
		}
		return null;
	}
	const { status, savedAt, error } = autosave.state;
	if (status === 'pending' || status === 'saving') return { label: 'Saving…', tone: 'saving' };
	// After a failed save the pill is a button that tries again straight away.
	if (status === 'error') return { label: 'Not saved · Try again', tone: 'error', title: saveFailureHint(error), retry: true };
	if (status === 'conflict') {
		return { label: 'Not saved', tone: 'error', title: 'This document was changed on another device.' };
	}
	if (status === 'saved') return { label: 'Saved', tone: 'saved', title: `Saved at ${formatSaveTime(savedAt)}` };
	return null;
});

// Client documents have no Save button; this saves straight away instead of at the next try.
const saveClientDocumentNow = async () => {
	try {
		await autosave.saveNow();
	} catch (error) {
		// A conflict is settled in its own dialog. Otherwise the pill stays "Not saved" and says why.
		if (!isClientDocumentConflict(error)) toast?.error?.(saveFailureHint(error));
	}
};

// Shows the latest saved version, dropping what was not saved here.
const loadLatestDocument = async () => {
	const latest = await clientDocumentsStore.fetchDocument(currentDocumentId.value, { forceRefresh: true });
	if (!latest) throw new Error('Document not found');
	documentRecord.value = latest;
	replaceContent(latest.content || {}, { documentType: latest.documentType });
	markPristine();
	applyTabTitle();
};

// The document was saved on another device while it was open here: the office chooses a version.
const conflict = reactive({ busy: false, error: '' });
const loadLatestAfterConflict = async () => {
	conflict.busy = true;
	conflict.error = '';
	try {
		await loadLatestDocument();
		autosave.reset('saved');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView loadLatestAfterConflict');
		conflict.error = 'The latest version could not be loaded. Try again.';
	} finally {
		conflict.busy = false;
	}
};
const keepMineAfterConflict = async () => {
	conflict.busy = true;
	conflict.error = '';
	try {
		const latest = await clientDocumentsStore.fetchDocument(currentDocumentId.value, { forceRefresh: true });
		// Saving over the newest revision replaces the changes made elsewhere with these.
		documentRecord.value = { ...documentRecord.value, revision: latest?.revision, updatedAt: latest?.updatedAt };
		autosave.reset('pending');
		await autosave.saveNow();
	} catch (error) {
		// Saved again elsewhere meanwhile: the dialog stays for another choice.
		if (!isClientDocumentConflict(error)) {
			logError?.(error, 'ClientDocumentEditorView keepMineAfterConflict');
			conflict.error = 'Your version could not be saved. Try again.';
		}
	} finally {
		conflict.busy = false;
	}
};

// An editor with nothing unsaved shows what was saved on another device meanwhile, when it comes
// back into view and every half minute. It never replaces work that is not saved yet.
const sameSavedVersion = (a, b) =>
	a?.revision !== undefined && b?.revision !== undefined
		? a.revision === b.revision
		: a?.updatedAt === b?.updatedAt;
useLiveRefresh(
	async ({ canApply }) => {
		if (isSupplierDocument.value || !currentDocumentId.value || loadFailed.value) return;
		const latest = await clientDocumentsStore.fetchDocument(currentDocumentId.value, { forceRefresh: true });
		if (!latest || !canApply() || sameSavedVersion(latest, documentRecord.value)) return;
		documentRecord.value = latest;
		replaceContent(latest.content || {}, { documentType: latest.documentType });
		markPristine();
		applyTabTitle();
	},
	{
		isBusy: () =>
			!ready.value ||
			hasUnsavedChanges.value ||
			saving.value ||
			['pending', 'saving', 'error', 'conflict'].includes(autosave.state.status) ||
			documentCheck.open ||
			sectionImport.open ||
			showPreview.value,
	},
);

const handleSave = async () => {
	if (!canSaveDocument.value) return;
	if (!isSupplierDocument.value) {
		await saveClientDocumentNow();
		return;
	}
	saving.value = true;
	errorMessage.value = '';
	try {
		const savePayload = buildSavePayload();
		const saved = await saveSupplierTemplateDocument(savePayload);
		documentRecord.value = saved;
		currentDocumentId.value = saved?.documentId || saved?.id || currentDocumentId.value;
		replaceContent({
			...(saved?.content || savePayload.content),
			footerText: savePayload.content.footerText,
		});
		markPristine();
		applyTabTitle();
		supplierSavedAt.value = new Date();
		toast?.success?.(`${getClientDocumentLabel(currentDocumentType.value)} saved.`);
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView handleSave');
		errorMessage.value = error?.response?.data?.error?.message || error?.message || 'Failed to save document';
		toast?.error?.(`Failed to save ${getClientDocumentLowerLabel(currentDocumentType.value)}.`);
	} finally {
		saving.value = false;
	}
};

const navigateBack = () => {
	emit('back-to-list', {
		tabId: myTabId.value,
		documentDomain: props.documentDomain,
		category: isSupplierDocument.value ? 'assignments' : null,
	});
};

const openPreview = () => {
	showPreview.value = true;
};

const closePreview = () => {
	showPreview.value = false;
};

// Section import: copies one section from another saved document of the same kind. The last import
// can be undone until the document is loaded again.
const sectionImport = reactive({ open: false, sectionId: '', loading: false, error: '', documents: [], returnFocus: null });

const sectionImportSection = computed(() =>
	getImportableSection(currentDocumentType.value, sectionImport.sectionId),
);
const sectionImportSources = computed(() =>
	sectionImportSection.value
		? buildSectionImportSources({
				section: sectionImportSection.value,
				documents: sectionImport.documents,
				currentContent: proposalContentForOutput.value,
			})
		: [],
);

const sectionImportTitle = (sectionId) => {
	const section = getImportableSection(currentDocumentType.value, sectionId);
	return `Import ${section?.label || 'this section'} from another ${documentLowerLabel.value}`;
};

const loadSectionImportDocuments = async () => {
	const documentType = currentDocumentType.value;
	const isAssignment = documentType === ASSIGNMENT_CONFIRMATION_DOCUMENT_TYPE;
	// Assignment confirmations moved to supplier documents are sources too; if they cannot be read,
	// the client documents are still offered.
	const [clientDocuments, supplierDocuments] = await Promise.all([
		clientDocumentsStore.peekAllDocuments(),
		isAssignment
			? supplierPortalAdminStore.peekDocuments().catch((error) => {
					logError?.(error, 'ClientDocumentEditorView loadSectionImportDocuments');
					return [];
				})
			: [],
	]);
	const currentKey = `${isSupplierDocument.value ? 'supplier' : 'client'}:${currentDocumentId.value}`;
	return [
		...clientDocuments
			.filter((document) => document.documentType === documentType)
			.map((document) => toSectionImportDocument(document, { documentType, origin: 'client' })),
		...supplierDocuments
			.filter((document) => document.templateKey === ASSIGNMENT_CONFIRMATION_TEMPLATE && document.content)
			.map((document) => toSectionImportDocument(document, { documentType, origin: 'supplier' })),
	].filter((document) => document.key !== currentKey);
};

const refreshSectionImportDocuments = async () => {
	sectionImport.loading = true;
	sectionImport.error = '';
	try {
		sectionImport.documents = await loadSectionImportDocuments();
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView refreshSectionImportDocuments');
		sectionImport.error = `Could not load the saved ${documentLowerLabel.value} documents.`;
	} finally {
		sectionImport.loading = false;
	}
};

const openSectionImport = (sectionId, event) => {
	if (!getImportableSection(currentDocumentType.value, sectionId)) return;
	Object.assign(sectionImport, {
		open: true,
		sectionId,
		documents: [],
		returnFocus: event?.currentTarget || null,
	});
	refreshSectionImportDocuments();
};

const closeSectionImport = () => {
	sectionImport.open = false;
};

const importSectionFrom = (sourceKey) => {
	const section = sectionImportSection.value;
	const source = sectionImport.documents.find((document) => document.key === sourceKey);
	if (!section || !source) return;
	const previous = importSection(form.content, source.content, section);
	sectionImportUndo.value = { sectionId: section.id, previous };
	closeSectionImport();
	toast?.success?.(`${section.label} imported from ${source.reference || `another ${documentLowerLabel.value}`}.`);
};

const undoSectionImport = () => {
	const undo = sectionImportUndo.value;
	const section = getImportableSection(currentDocumentType.value, undo?.sectionId);
	sectionImportUndo.value = null;
	if (section) writeSectionValues(form.content, section, undo.previous);
};

// Check document: the server proofreads the text with Claude and returns suggestions; each one is
// applied only when the office presses Fix.
const DOCUMENT_CHECK_MIN_MS = 900;
const documentCheck = reactive({ open: false, status: 'idle', findings: [], fieldsChecked: 0, error: '' });

const documentCheckFailure = (error) => {
	// A server without the check answers 405 (the path matches a document id) or 403/404.
	if ([403, 404, 405].includes(error?.response?.status)) {
		return 'Document check arrives with the next server update.';
	}
	return error?.response?.data?.error?.message || 'The document check could not be completed. Try again in a moment.';
};

const runDocumentCheck = async () => {
	if (documentCheck.status === 'checking') return;
	Object.assign(documentCheck, { open: true, status: 'checking', findings: [], fieldsChecked: 0, error: '' });
	const started = Date.now();
	let outcome;
	try {
		const result = await clientDocumentsStore.proofreadDocument({
			content: JSON.parse(JSON.stringify(form.content)),
			documentType: currentDocumentType.value,
		});
		outcome = {
			status: 'done',
			findings: (result.findings || []).map((finding) => ({ ...finding, state: 'open' })),
			fieldsChecked: result.fieldsChecked || 0,
		};
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView runDocumentCheck');
		outcome = { status: 'error', error: documentCheckFailure(error) };
	}
	// The scan stays a moment, so a quick answer does not flash past.
	const wait = DOCUMENT_CHECK_MIN_MS - (Date.now() - started);
	if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
	Object.assign(documentCheck, outcome);
};

const fixDocumentFinding = (finding) => {
	if (applyFinding(form.content, finding)) finding.state = 'fixed';
};

const fixAllDocumentFindings = () => {
	documentCheck.findings
		.filter((finding) => finding.state === 'open' && finding.suggestion)
		.forEach(fixDocumentFinding);
};

const downloadPdf = async () => {
	if (pdfGenerating.value) return;
	pdfGenerating.value = true;
	errorMessage.value = '';
	try {
		await downloadProposalPdfFromHtml({
			html: previewHtml.value,
			filename: previewPdfFilename.value,
			logError,
		});
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView downloadPdf');
		errorMessage.value = error?.message || 'Failed to generate PDF';
		toast?.error?.('Failed to download PDF.');
	} finally {
		pdfGenerating.value = false;
	}
};

const addTextEntry = (list) => {
	if (!Array.isArray(list)) return;
	list.push('');
};

const listToText = (list = []) => (Array.isArray(list) ? list.join('\n') : '');

const updateStringList = (target, key, value) => {
	if (!target || !key) return;
	const lines = String(value || '').split(/\r?\n/);
	target[key] = lines.length ? lines : [''];
};

const {
	handleBulletedTextareaBackspace,
	handleBulletedTextareaDelete,
	handleBulletedTextareaEnter,
	updateBulletedStringList,
} = createBulletedTextareaEditor({ scheduleResize: scheduleTextareaResize });

const removeAt = (list, index) => {
	if (!Array.isArray(list)) return;
	list.splice(index, 1);
};

const proposalRowsForGroup = (group) => {
	if (group === 'schedule') return form.content?.schedule?.rows || [];
	if (group === 'text-sections') return form.content?.textSections || [];
	return [];
};

const moveProposalRow = (group, fromIndex, toIndex) => {
	const rows = proposalRowsForGroup(group);
	if (
		!Array.isArray(rows) ||
		fromIndex < 0 ||
		toIndex < 0 ||
		fromIndex >= rows.length ||
		toIndex >= rows.length ||
		fromIndex === toIndex
	) {
		return false;
	}
	const [row] = rows.splice(fromIndex, 1);
	rows.splice(toIndex, 0, row);
	return true;
};

const moveProposalRowBy = (group, index, offset) => {
	const targetIndex = index + offset;
	if (!moveProposalRow(group, index, targetIndex)) return;
	nextTick(() => {
		const handles = document.querySelectorAll(
			`[data-proposal-reorder-group="${group}"] .row-move-handle`,
		);
		handles[targetIndex]?.focus?.({ preventScroll: true });
	});
};

const isProposalRowDragging = (group, index) =>
	proposalRowDrag.active && proposalRowDrag.group === group && proposalRowDrag.index === index;

const resetProposalRowDrag = () => {
	proposalRowDrag.group = '';
	proposalRowDrag.index = -1;
	proposalRowDrag.pointerId = null;
	proposalRowDrag.startX = 0;
	proposalRowDrag.startY = 0;
	proposalRowDrag.active = false;
};

const finishProposalRowDrag = (event) => {
	if (
		proposalRowDrag.pointerId !== null &&
		event?.pointerId !== undefined &&
		event.pointerId !== proposalRowDrag.pointerId
	) {
		return;
	}
	window.removeEventListener('pointermove', handleProposalRowDragMove);
	window.removeEventListener('pointerup', finishProposalRowDrag);
	window.removeEventListener('pointercancel', finishProposalRowDrag);
	resetProposalRowDrag();
};

function handleProposalRowDragMove(event) {
	if (event.pointerId !== proposalRowDrag.pointerId || !proposalRowDrag.group) return;
	const distance = Math.hypot(
		event.clientX - proposalRowDrag.startX,
		event.clientY - proposalRowDrag.startY,
	);
	if (!proposalRowDrag.active && distance < 4) return;
	proposalRowDrag.active = true;
	event.preventDefault();

	const targetRow = document
		.elementFromPoint?.(event.clientX, event.clientY)
		?.closest?.('[data-proposal-reorder-group]');
	if (targetRow?.dataset?.proposalReorderGroup !== proposalRowDrag.group) return;
	const targetIndex = Number(targetRow.dataset.proposalReorderIndex);
	if (!Number.isInteger(targetIndex) || targetIndex === proposalRowDrag.index) return;
	if (moveProposalRow(proposalRowDrag.group, proposalRowDrag.index, targetIndex)) {
		proposalRowDrag.index = targetIndex;
	}
}

const startProposalRowDrag = (event, group, index) => {
	if (event.pointerType === 'mouse' && event.button !== 0) return;
	finishProposalRowDrag();
	proposalRowDrag.group = group;
	proposalRowDrag.index = index;
	proposalRowDrag.pointerId = event.pointerId;
	proposalRowDrag.startX = event.clientX;
	proposalRowDrag.startY = event.clientY;
	event.currentTarget?.setPointerCapture?.(event.pointerId);
	window.addEventListener('pointermove', handleProposalRowDragMove, { passive: false });
	window.addEventListener('pointerup', finishProposalRowDrag);
	window.addEventListener('pointercancel', finishProposalRowDrag);
};

const findCatalogOption = (options = [], id) =>
	(options || []).find((option) => String(option?.id) === String(id)) || null;

const findCatalogItem = (options = [], id) =>
	catalogStore.getItemById?.(id) || findCatalogOption(options, id);

const optionName = (option = {}) =>
	(option.catalogDisplayName || option.name || option.label || option.attributes?.name || '').trim();

const optionAddress = (option = {}) =>
	option.address || option.attributes?.address || '';

const optionPhone = (option = {}) =>
	option.telephone || option.phone || option.phoneNumber || option.attributes?.telephone || option.attributes?.phone || '';

const optionSupplierRate = (option = {}) =>
	option.supplierRate ?? option.attributes?.supplierRate ?? null;

const applyAssignmentHourlyRate = (option = {}) => {
	const rate = optionSupplierRate(option);
	if (rate === null || rate === undefined || rate === '') return;
	const currentRate = Number(form.content?.pricing?.hourlyRate || 0);
	if (!currentRate || currentRate === 13.5) {
		form.content.pricing.hourlyRate = Number(rate);
	}
};

const onQuotationClientSelected = (clientId) => {
	form.content.clientId = clientId;
	const client = findCatalogItem(clientOptions.value, clientId);
	form.content.clientName = client ? optionName(client) : '';
	if (!client) return;
	form.content.clientAddress = optionAddress(client) || form.content.clientAddress || '';
};

const onProposalClientSelected = (clientId) => {
	form.content.clientId = clientId;
	const client = findCatalogItem(clientOptions.value, clientId);
	form.content.clientName = client ? optionName(client) : '';
};

const onProposalBuildingSelected = (property, buildingId) => {
	if (!property) return;
	property.buildingId = buildingId ?? null;
	const building = findCatalogItem(buildingOptions.value, buildingId);
	property.name = building ? optionName(building) : '';
	property.address = building ? optionAddress(building) : '';
};

const onQuotationCleaningStyleChanged = (value) => {
	form.content.subtitle = value;
};

const onQuotationCleaningStyleSelected = (value) => {
	form.content.subtitle = value;
	form.content.serviceDetails.serviceId = null;
	form.content.serviceDetails.serviceType = value;
};

const onQuotationServiceTypeChanged = (value) => {
	form.content.serviceDetails.serviceId = null;
	form.content.serviceDetails.serviceType = value;
};

const onQuotationPropertyChanged = (value) => {
	form.content.serviceDetails.propertyId = null;
	form.content.serviceDetails.property = value;
};

const onQuotationSiteAddressChanged = (value) => {
	form.content.serviceDetails.siteAddress = value;
};

const saveQuotationCleaningStyleOptions = async (options) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving cleaning style values.');
	}

	const documentPresets = mergeQuotationCleaningStyleOptions(
		project?.documentPresets,
		options,
	);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		toast?.success?.('Cleaning style catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveQuotationCleaningStyleOptions');
		toast?.error?.('Failed to save cleaning style catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the cleaning style catalog.',
		);
	}
};

const saveQuotationPropertyOptions = async (options) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving property values.');
	}

	const documentPresets = mergeQuotationPropertyOptions(
		project?.documentPresets,
		options,
	);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		toast?.success?.('Property catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveQuotationPropertyOptions');
		toast?.error?.('Failed to save property catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the property catalog.',
		);
	}
};

const saveQuotationSiteAddressOptions = async (options) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving site address values.');
	}

	const documentPresets = mergeQuotationSiteAddressOptions(
		project?.documentPresets,
		options,
	);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		toast?.success?.('Site address catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveQuotationSiteAddressOptions');
		toast?.error?.('Failed to save site address catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the site address catalog.',
		);
	}
};

const saveQuotationCostItemOptions = async (options) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving cost item values.');
	}

	const documentPresets = mergeQuotationCostItemOptions(
		project?.documentPresets,
		options,
	);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		toast?.success?.('Cost item catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveQuotationCostItemOptions');
		toast?.error?.('Failed to save cost item catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the cost item catalog.',
		);
	}
};

const onAssignmentSupplierSelected = (supplierId) => {
	form.content.subcontractorId = supplierId;
	const supplier = findCatalogItem(supplierOptions.value, supplierId);
	if (!supplier) return;
	form.content.subcontractorName = optionName(supplier);
	form.content.subcontractorMobile = optionPhone(supplier) || form.content.subcontractorMobile || '';
	applyAssignmentHourlyRate(supplier);
	const firstSignatory = form.content.acceptance?.signatories?.[0];
	if (firstSignatory) {
		firstSignatory.name = form.content.subcontractorName;
		firstSignatory.mobile = form.content.subcontractorMobile;
	}
};

const onAssignmentPropertySelected = (propertyId) => {
	form.content.propertyId = propertyId;
	const property = findCatalogItem(buildingOptions.value, propertyId);
	if (!property) return;
	form.content.propertyName = optionName(property);
	form.content.propertyAddress = optionAddress(property) || form.content.propertyAddress || '';
};

const onAssignmentSubtitleChanged = (value) => {
	assignmentSubtitleTouched.value = true;
	form.content.subtitle = value;
};

const saveAssignmentSubtitleOptions = async (options) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving subtitle values.');
	}

	const documentPresets = mergeAssignmentSubtitleOptions(project?.documentPresets, options);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		toast?.success?.('Subtitle values saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveAssignmentSubtitleOptions');
		toast?.error?.('Failed to save subtitle values.');
		throw new Error(
			error?.response?.data?.error?.message || error?.message || 'Could not save subtitle values.',
		);
	}
};

const saveAssignmentServiceAreaOptions = async (options) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving service area values.');
	}

	const documentPresets = mergeAssignmentServiceAreaOptions(project?.documentPresets, options);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		toast?.success?.('Service area catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveAssignmentServiceAreaOptions');
		toast?.error?.('Failed to save service area catalog.');
		throw new Error(
			error?.response?.data?.error?.message || error?.message || 'Could not save service area values.',
		);
	}
};

const saveProposalServiceAreaItemsTemplate = async (items) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving service area bullets.');
	}

	const documentPresets = mergeProposalServiceAreaItemsTemplate(
		project?.documentPresets,
		items,
	);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		for (const area of form.content.serviceAreas) {
			area.items = appendNewProposalServiceAreaItemsFromCatalog(
				proposalServiceAreaItemsCatalogSnapshot ?? proposalServiceAreaItemsTemplate.value,
				items,
				area.items,
			);
		}
		proposalServiceAreaItemsCatalogSnapshot = null;
		toast?.success?.('Service area bullet catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveProposalServiceAreaItemsTemplate');
		toast?.error?.('Failed to save service area bullet catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the service area bullet catalog.',
		);
	}
};

const saveProposalTaskItemsTemplate = async ({
	items,
	title,
	fallbackIndex,
	snapshot,
	mergeTemplate,
	successMessage,
	errorMessage: saveErrorMessage,
}) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error(`No active project is available for saving ${title.toLocaleLowerCase()}.`);
	}

	const documentPresets = mergeTemplate(project?.documentPresets, items);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		const taskSections = form.content.taskSections || [];
		const currentSection =
			findProposalTaskSection(taskSections, title) || taskSections[fallbackIndex] || null;
		if (currentSection) {
			currentSection.items = appendNewProposalTaskItemsFromCatalog(
				snapshot,
				items,
				currentSection.items,
			);
		}
		toast?.success?.(successMessage);
	} catch (error) {
		logError?.(error, `ClientDocumentEditorView saveProposal${title.replace(/\s+/g, '')}Template`);
		toast?.error?.(saveErrorMessage);
		throw new Error(
			error?.response?.data?.error?.message || error?.message || saveErrorMessage,
		);
	}
};

const saveProposalWeeklyTasksTemplate = async (items) => {
	await saveProposalTaskItemsTemplate({
		items,
		title: 'Weekly Tasks',
		fallbackIndex: 0,
		snapshot: proposalWeeklyTasksCatalogSnapshot ?? proposalWeeklyTasksTemplate.value,
		mergeTemplate: mergeProposalWeeklyTasksTemplate,
		successMessage: 'Weekly tasks catalog saved.',
		errorMessage: 'Failed to save weekly tasks catalog.',
	});
	proposalWeeklyTasksCatalogSnapshot = null;
};

const saveProposalMonthlyTasksTemplate = async (items) => {
	await saveProposalTaskItemsTemplate({
		items,
		title: 'Monthly Tasks',
		fallbackIndex: 1,
		snapshot: proposalMonthlyTasksCatalogSnapshot ?? proposalMonthlyTasksTemplate.value,
		mergeTemplate: mergeProposalMonthlyTasksTemplate,
		successMessage: 'Monthly tasks catalog saved.',
		errorMessage: 'Failed to save monthly tasks catalog.',
	});
	proposalMonthlyTasksCatalogSnapshot = null;
};

const saveProposalPricingNotesTemplate = async (notes) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving proposal pricing notes.');
	}

	const documentPresets = mergeProposalPricingNotesTemplate(
		project?.documentPresets,
		notes,
	);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		form.content.pricing.notes = appendNewProposalTaskItemsFromCatalog(
			proposalPricingNotesCatalogSnapshot ?? proposalPricingNotesTemplate.value,
			notes,
			form.content.pricing.notes,
		);
		proposalPricingNotesCatalogSnapshot = null;
		toast?.success?.('Proposal pricing notes catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveProposalPricingNotesTemplate');
		toast?.error?.('Failed to save proposal pricing notes catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the proposal pricing notes catalog.',
		);
	}
};

const saveAssignmentRegularTasksTemplate = async (tasks) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving regular cleaning tasks.');
	}

	const documentPresets = mergeAssignmentRegularTasksTemplate(project?.documentPresets, tasks);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		const currentTasks = form.content.scope.regularTasks;
		const tasksWithNewCatalogItems = appendNewAssignmentRegularTasksFromCatalog(
			assignmentRegularTasksCatalogSnapshot ?? assignmentRegularTasksTemplate.value,
			tasks,
			currentTasks,
		);
		if (tasksWithNewCatalogItems.length > currentTasks.length) {
			form.content.scope.regularTasks = tasksWithNewCatalogItems;
		}
		assignmentRegularTasksCatalogSnapshot = null;
		toast?.success?.('Regular cleaning tasks catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveAssignmentRegularTasksTemplate');
		toast?.error?.('Failed to save regular cleaning tasks catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the regular cleaning tasks catalog.',
		);
	}
};

const saveAssignmentMonthlyTasksTemplate = async (tasks) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving monthly tasks.');
	}

	const documentPresets = mergeAssignmentMonthlyTasksTemplate(project?.documentPresets, tasks);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		const currentTasks = form.content.scope.monthlyTasks;
		const tasksWithNewCatalogItems = appendNewAssignmentMonthlyTasksFromCatalog(
			assignmentMonthlyTasksCatalogSnapshot ?? assignmentMonthlyTasksTemplate.value,
			tasks,
			currentTasks,
		);
		if (tasksWithNewCatalogItems.length > currentTasks.length) {
			form.content.scope.monthlyTasks = tasksWithNewCatalogItems;
		}
		assignmentMonthlyTasksCatalogSnapshot = null;
		toast?.success?.('Monthly tasks catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveAssignmentMonthlyTasksTemplate');
		toast?.error?.('Failed to save monthly tasks catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the monthly tasks catalog.',
		);
	}
};

const saveAssignmentPricingNotesTemplate = async (notes) => {
	const project = projectsStore.activeProject;
	const projectDocumentId = project?.documentId || project?.id;
	if (!projectDocumentId) {
		throw new Error('No active project is available for saving pricing notes.');
	}

	const documentPresets = mergeAssignmentPricingNotesTemplate(project?.documentPresets, notes);
	try {
		await projectsStore.updateProject({
			documentId: projectDocumentId,
			data: { documentPresets },
		});
		const currentNotes = form.content.pricing.notes;
		const notesWithNewCatalogItems = appendNewAssignmentPricingNotesFromCatalog(
			assignmentPricingNotesCatalogSnapshot ?? assignmentPricingNotesTemplate.value,
			notes,
			currentNotes,
		);
		if (notesWithNewCatalogItems.length > currentNotes.length) {
			form.content.pricing.notes = notesWithNewCatalogItems;
		}
		assignmentPricingNotesCatalogSnapshot = null;
		toast?.success?.('Pricing notes catalog saved.');
	} catch (error) {
		logError?.(error, 'ClientDocumentEditorView saveAssignmentPricingNotesTemplate');
		toast?.error?.('Failed to save pricing notes catalog.');
		throw new Error(
			error?.response?.data?.error?.message ||
				error?.message ||
				'Could not save the pricing notes catalog.',
		);
	}
};

const onAssignmentServiceSelected = (serviceId) => {
	form.content.serviceId = serviceId;
	const service = findCatalogItem(serviceOptions.value, serviceId);
	if (!service) return;
	const name = optionName(service);
	form.content.serviceType = name;
	if (
		!assignmentSubtitleTouched.value &&
		(!form.content.subtitle || form.content.subtitle === DEFAULT_ASSIGNMENT_CONFIRMATION_SUBTITLE)
	) {
		form.content.subtitle = name;
	}
	applyAssignmentHourlyRate(service);
};

const addQuotationPhase = () => {
	const nextIndex = (form.content.schedule?.phases || []).length + 1;
	form.content.schedule.phases.push({
		id: `phase-${Date.now()}`,
		title: `Phase ${nextIndex}`,
		date: '',
		team: '',
		time: '',
	});
};

const addQuotationCostRow = () => {
	form.content.costBreakdown.rows.push({
		id: `cost-${Date.now()}`,
		item: '',
		amount: '',
	});
};

const onQuotationCostItemChanged = (index, value) => {
	const row = form.content.costBreakdown.rows[index];
	if (row) row.item = value;
};

const openQuotationCostItemManager = () => {
	quotationCostItemManagerRef.value?.open?.({
		returnFocus: quotationCostItemManagerButtonRef.value,
	});
};

const addProperty = () => {
	form.content.properties.push({ buildingId: null, name: '', address: '' });
};

const addServiceArea = () => {
	const usedPropertyNames = new Set(
		form.content.serviceAreas
			.map((area) => String(area?.title || '').trim().toLocaleLowerCase())
			.filter(Boolean),
	);
	const nextProperty = form.content.properties.find((property) => {
		const name = String(property?.name || '').trim();
		return name && !usedPropertyNames.has(name.toLocaleLowerCase());
	});
	form.content.serviceAreas.push({
		title: nextProperty?.name || '',
		items: [...proposalServiceAreaItemsTemplate.value],
	});
};

const addTaskSection = () => {
	const taskSections = form.content.taskSections;
	if (!findProposalTaskSection(taskSections, 'Weekly Tasks')) {
		taskSections.push({
			title: 'Weekly Tasks',
			items: [...proposalWeeklyTasksTemplate.value],
			footnote: '',
		});
		return;
	}
	if (!findProposalTaskSection(taskSections, 'Monthly Tasks')) {
		taskSections.push({
			title: 'Monthly Tasks',
			items: [...proposalMonthlyTasksTemplate.value],
			footnote: DEFAULT_PROPOSAL_MONTHLY_TASKS_FOOTNOTE,
		});
		return;
	}
	taskSections.push({ title: '', items: [''], footnote: '' });
};

const addScheduleRow = () => {
	form.content.schedule.rows.push({ label: '', value: '' });
};

// A small format bar floats over the cleaning schedule row being edited (proposals and
// assignment confirmations): bold, italic, black or blue.
const scheduleFormatRow = ref(null);
const openScheduleRowFormat = (row) => {
	scheduleFormatRow.value = row;
};
const closeScheduleRowFormatOnBlur = (event) => {
	const next = event.relatedTarget;
	if (next && !next.closest?.('.row-format-table')) scheduleFormatRow.value = null;
};
const closeScheduleRowFormatOutside = (event) => {
	if (scheduleFormatRow.value && !event.target?.closest?.('.row-format-table')) {
		scheduleFormatRow.value = null;
	}
};
const scheduleRowFormatClass = (row) => ({
	'is-bold': Boolean(row.bold),
	'is-italic': Boolean(row.italic),
	'is-blue': row.color === 'blue',
});
const toggleScheduleRowStyle = (row, style) => {
	if (row[style]) delete row[style];
	else row[style] = true;
};
// The total row adds up the hours of the block rows. Only one row is the total, and it is bold.
// A row marked by mistake gets its own value and weight back when the mark comes off, until the
// document is loaded again.
const scheduleRowBeforeTotal = new WeakMap();
const clearScheduleRowTotal = (row) => {
	const before = scheduleRowBeforeTotal.get(toRaw(row));
	scheduleRowBeforeTotal.delete(toRaw(row));
	delete row.total;
	if (before) row.value = before.value;
	if (before?.bold) row.bold = true;
	else delete row.bold;
};
const toggleScheduleRowBlock = (row) => {
	if (row.block) {
		delete row.block;
		delete row.hourlyRate;
		return;
	}
	if (row.total) clearScheduleRowTotal(row);
	row.block = true;
	row.hourlyRate = toNumberOrNull(form.content.pricing?.hourlyRate);
};
const toggleScheduleRowTotal = (row) => {
	if (row.total) {
		clearScheduleRowTotal(row);
		return;
	}
	(form.content.schedule?.rows || []).filter((other) => other.total).forEach(clearScheduleRowTotal);
	scheduleRowBeforeTotal.set(toRaw(row), { value: row.value, bold: Boolean(row.bold) });
	delete row.block;
	delete row.hourlyRate;
	row.total = true;
	row.bold = true;
};
const onScheduleRowToggle = (row, style) => {
	if (style === 'block') toggleScheduleRowBlock(row);
	else if (style === 'total') toggleScheduleRowTotal(row);
	else toggleScheduleRowStyle(row, style);
};
// The total row's value follows the block rows' hours as they change.
watch(
	() => {
		const rows = form.content.schedule?.rows || [];
		const totalRow = rows.find((row) => row.total);
		return totalRow ? { row: totalRow, value: getScheduleTotalValue(rows) } : null;
	},
	(next) => {
		if (next && next.row.value !== next.value) next.row.value = next.value;
	},
	{ immediate: true },
);
const setScheduleRowColor = (row, color) => {
	if (color) row.color = color;
	else delete row.color;
};

const setAssignmentServiceAreaInputRef = (component, index) => {
	assignmentServiceAreaInputRefs.value[index] = component || null;
};

const closeAssignmentServiceAreaDropdowns = () => {
	assignmentServiceAreaInputRefs.value.forEach((component) => component?.closeDropdown?.());
};

const onAssignmentServiceAreaChanged = (index, value) => {
	form.content.scope.serviceAreas[index] = value;
};

const addAssignmentServiceArea = () => {
	closeAssignmentServiceAreaDropdowns();
	form.content.scope.serviceAreas.push('');
	const index = form.content.scope.serviceAreas.length - 1;
	nextTick(() => assignmentServiceAreaInputRefs.value[index]?.focus?.());
};

const removeAssignmentServiceArea = (index) => {
	closeAssignmentServiceAreaDropdowns();
	assignmentServiceAreaInputRefs.value = [];
	form.content.scope.serviceAreas.splice(index, 1);
	const nextIndex = Math.min(index, form.content.scope.serviceAreas.length - 1);
	nextTick(() => {
		if (nextIndex >= 0) {
			assignmentServiceAreaInputRefs.value[nextIndex]?.focus?.();
		} else {
			assignmentServiceAreaAddButtonRef.value?.focus?.({ preventScroll: true });
		}
	});
};

const openAssignmentServiceAreaManager = () => {
	closeAssignmentServiceAreaDropdowns();
	assignmentServiceAreaManagerRef.value?.open?.({
		returnFocus: assignmentServiceAreaManagerButtonRef.value,
	});
};

const openAssignmentRegularTasksManager = () => {
	assignmentRegularTasksCatalogSnapshot = [...assignmentRegularTasksTemplate.value];
	assignmentRegularTasksManagerRef.value?.open?.({
		returnFocus: assignmentRegularTasksManagerButtonRef.value,
	});
};

const openProposalServiceAreaManager = () => {
	proposalServiceAreaItemsCatalogSnapshot = [...proposalServiceAreaItemsTemplate.value];
	proposalServiceAreaManagerRef.value?.open?.({
		returnFocus: proposalServiceAreaManagerButtonRef.value,
	});
};

const openProposalWeeklyTasksManager = () => {
	proposalWeeklyTasksCatalogSnapshot = [...proposalWeeklyTasksTemplate.value];
	proposalWeeklyTasksManagerRef.value?.open?.({
		returnFocus: proposalWeeklyTasksManagerButtonRef.value,
	});
};

const openProposalMonthlyTasksManager = () => {
	proposalMonthlyTasksCatalogSnapshot = [...proposalMonthlyTasksTemplate.value];
	proposalMonthlyTasksManagerRef.value?.open?.({
		returnFocus: proposalMonthlyTasksManagerButtonRef.value,
	});
};

const openProposalPricingNotesManager = () => {
	proposalPricingNotesCatalogSnapshot = [...proposalPricingNotesTemplate.value];
	proposalPricingNotesManagerRef.value?.open?.({
		returnFocus: proposalPricingNotesManagerButtonRef.value,
	});
};

const openAssignmentMonthlyTasksManager = () => {
	assignmentMonthlyTasksCatalogSnapshot = [...assignmentMonthlyTasksTemplate.value];
	assignmentMonthlyTasksManagerRef.value?.open?.({
		returnFocus: assignmentMonthlyTasksManagerButtonRef.value,
	});
};

const openAssignmentPricingNotesManager = () => {
	assignmentPricingNotesCatalogSnapshot = [...assignmentPricingNotesTemplate.value];
	assignmentPricingNotesManagerRef.value?.open?.({
		returnFocus: assignmentPricingNotesManagerButtonRef.value,
	});
};

const toggleAssignmentMonthlyTasks = () => {
	form.content.scope.monthlyTasksEnabled = !form.content.scope.monthlyTasksEnabled;
};

const addAssignmentScheduleRow = () => {
	form.content.schedule.rows.push({ label: '', value: '' });
};

const addTextSection = () => {
	form.content.textSections.push({
		id: `custom-${Date.now()}`,
		title: '',
		paragraphs: [''],
	});
};

const addSignatory = () => {
	form.content.acceptance.signatories.push({
		label: 'Signed for and on behalf of',
		name: '',
		role: '',
	});
};

const addAssignmentSignatory = () => {
	form.content.acceptance.signatories.push({
		label: '',
		name: '',
		role: '',
		mobile: '',
		signatureLabel: 'Signature',
		dateLabel: 'Date',
	});
};

const formatCurrency = (value) => formatGbpAmount(value);

const assignmentOutlineSections = [
	{ id: 'assignment-sec-details', label: 'Details' },
	{ id: 'assignment-sec-areas', label: 'Service areas' },
	{ id: 'assignment-sec-regular-tasks', label: 'Regular tasks' },
	{ id: 'assignment-sec-monthly-tasks', label: 'Monthly tasks' },
	{ id: 'assignment-sec-schedule', label: 'Cleaning schedule' },
	{ id: 'assignment-sec-pricing', label: 'Pricing' },
	{ id: 'assignment-sec-site', label: 'Site information' },
	{ id: 'assignment-sec-terms', label: 'Terms' },
	{ id: 'assignment-sec-contact', label: 'Contact' },
	{ id: 'assignment-sec-acceptance', label: 'Acceptance' },
	{ id: 'assignment-sec-signatures', label: 'Signatures' },
	{ id: 'assignment-sec-footer', label: 'Document footer' },
];
const proposalOutlineSections = [
	{ id: 'proposal-sec-details', label: 'Details' },
	{ id: 'proposal-sec-summary', label: 'Summary' },
	{ id: 'proposal-sec-areas', label: 'Service areas' },
	{ id: 'proposal-sec-tasks', label: 'Tasks' },
	{ id: 'proposal-sec-scope', label: 'Scope clarification' },
	{ id: 'proposal-sec-schedule', label: 'Cleaning schedule' },
	{ id: 'proposal-sec-pricing', label: 'Pricing' },
	{ id: 'proposal-sec-text', label: 'Text sections' },
	{ id: 'proposal-sec-acceptance', label: 'Acceptance' },
	{ id: 'proposal-sec-footer', label: 'Document footer' },
];
const quotationOutlineSections = [
	{ id: 'quotation-sec-details', label: 'Details' },
	{ id: 'quotation-sec-client', label: 'Client and service' },
	{ id: 'quotation-sec-schedule', label: 'Work schedule' },
	{ id: 'quotation-sec-costs', label: 'Cost breakdown' },
	{ id: 'quotation-sec-payment-terms', label: 'Payment terms' },
	{ id: 'quotation-sec-acceptance', label: 'Acceptance' },
	{ id: 'quotation-sec-footer', label: 'Document footer' },
];
const outlineSections = computed(() => {
	if (isAssignmentConfirmation.value) return assignmentOutlineSections;
	if (isQuotation.value) return quotationOutlineSections;
	return proposalOutlineSections;
});
const activeOutlineSectionId = ref('');
let outlineScrollTarget = null;
let outlineSectionEls = [];
let outlineFrame = null;

// The same section ids can exist in a hidden inactive tab; prefer the rendered one.
const findRenderedSectionEl = (id) => {
	const candidates = Array.from(document.querySelectorAll(`#${id}`));
	return candidates.find((el) => el.offsetParent !== null) || candidates[0] || null;
};

const findScrollParent = (el) => {
	let node = el?.parentElement;
	while (node && node !== document.body) {
		const style = window.getComputedStyle(node);
		if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
			return node;
		}
		node = node.parentElement;
	}
	return null;
};

const updateActiveOutlineSection = () => {
	outlineFrame = null;
	if (!outlineSectionEls.length) return;
	const rootTop = outlineScrollTarget ? outlineScrollTarget.getBoundingClientRect().top : 0;
	const rootHeight = outlineScrollTarget ? outlineScrollTarget.clientHeight : window.innerHeight;
	const threshold = rootTop + rootHeight * 0.35;
	let current = outlineSectionEls[0].id;
	for (const el of outlineSectionEls) {
		if (el.getBoundingClientRect().top <= threshold) {
			current = el.id;
		}
	}
	activeOutlineSectionId.value = current;
};

const scheduleOutlineUpdate = () => {
	if (outlineFrame !== null || typeof window === 'undefined') return;
	outlineFrame = window.requestAnimationFrame(updateActiveOutlineSection);
};

const teardownOutlineTracking = () => {
	if (typeof window === 'undefined') return;
	(outlineScrollTarget || window).removeEventListener('scroll', scheduleOutlineUpdate);
	if (outlineFrame !== null) {
		window.cancelAnimationFrame(outlineFrame);
		outlineFrame = null;
	}
	outlineScrollTarget = null;
	outlineSectionEls = [];
};

const setupOutlineTracking = async () => {
	if (typeof window === 'undefined') return;
	await nextTick();
	teardownOutlineTracking();
	outlineSectionEls = outlineSections.value
		.map((section) => findRenderedSectionEl(section.id))
		.filter(Boolean);
	if (!outlineSectionEls.length) return;
	outlineScrollTarget = findScrollParent(outlineSectionEls[0]);
	(outlineScrollTarget || window).addEventListener('scroll', scheduleOutlineUpdate, { passive: true });
	updateActiveOutlineSection();
};

const scrollToOutlineSection = (id) => {
	const target = findRenderedSectionEl(id);
	if (!target) return;
	const scrollParent = findScrollParent(target);
	if (!scrollParent) {
		target.scrollIntoView({ behavior: 'auto', block: 'start' });
		return;
	}
	const parentRect = scrollParent.getBoundingClientRect();
	const targetRect = target.getBoundingClientRect();
	const top = scrollParent.scrollTop + targetRect.top - parentRect.top - 16;
	scrollParent.scrollTo({ top: Math.max(0, top), behavior: 'auto' });
};

watch(
	[ready, outlineSections],
	([isReady, sections]) => {
		if (isReady && sections.length) {
			void setupOutlineTracking();
		} else {
			teardownOutlineTracking();
		}
	},
);

onUnmounted(() => {
	teardownOutlineTracking();
	finishProposalRowDrag();
	document.removeEventListener('pointerdown', closeScheduleRowFormatOutside);
});

watch(
	() => form.content,
	() => {
		hasUnsavedChanges.value = pristineSnapshot.value
			? serializeSnapshot() !== pristineSnapshot.value
			: false;
		if (hasUnsavedChanges.value) autosave.schedule();
	},
	{ deep: true },
);

onMounted(() => {
	void loadDocument();
	document.addEventListener('pointerdown', closeScheduleRowFormatOutside);
});
</script>

<style scoped>
:deep(.invoice-creator.client-document-editor-layout) {
	padding: 24px 24px 120px;
	min-height: 100%;
	background: transparent;
}

:deep(.creator-footer) {
	position: fixed;
	z-index: 90;
	left: calc(220px + 24px);
	bottom: 22px;
	transform: none;
	display: flex;
	align-items: center;
	justify-content: flex-start;
	flex-wrap: wrap;
	gap: 20px;
	width: max-content;
	max-width: calc(100vw - 268px);
	margin: 0;
	padding: 12px 18px;
	border: 1px solid rgba(255, 255, 255, 0.58);
	border-radius: 12px;
	background:
		linear-gradient(180deg, rgba(255, 255, 255, 0.74), rgba(255, 255, 255, 0.54));
	box-shadow:
		0 18px 42px rgba(15, 23, 42, 0.13),
		0 2px 8px rgba(15, 23, 42, 0.06),
		inset 0 1px 0 rgba(255, 255, 255, 0.72);
	backdrop-filter: blur(22px) saturate(1.35);
	-webkit-backdrop-filter: blur(22px) saturate(1.35);
}

:deep(.creator-footer button) {
	min-height: 32px;
}

.document-details-panel,
.proposal-form {
	--proposal-input-width: 250px;
	--editor-column-width: 720px;
	--proposal-description-width: 500px;
	--proposal-control-height: 38px;
	--proposal-table-cell-gap: 8px;
	--proposal-control-border: 1px solid var(--border-color);
	--proposal-control-radius: 8px;
	--proposal-control-shadow: var(--box-shadow);
	--proposal-control-background: var(--noc-background, #fff);
	--document-input-font-size: 0.85rem;
	--catalog-input-border: var(--proposal-control-border);
	--catalog-input-border-radius: var(--proposal-control-radius);
	--catalog-input-height: var(--proposal-control-height);
	--catalog-input-padding: 6px 12px;
	--catalog-input-font-size: var(--document-input-font-size);
	--catalog-input-background: var(--proposal-control-background);
	--catalog-input-color: var(--text-color, #333);
	--catalog-input-font-family: inherit;
	--catalog-input-box-shadow: var(--proposal-control-shadow);
	--catalog-input-hover-border-color: var(--border-color-active);
	--catalog-input-focus-border-color: var(--text-color, #333);
	--catalog-input-focus-box-shadow: var(--proposal-control-shadow);
}

.document-details-panel,
.proposal-form {
	display: flex;
	flex-direction: column;
	gap: 16px;
	align-items: flex-start;
	min-width: 0;
	max-width: 100%;
}

.quotation-field-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 14px 16px;
	width: min(var(--editor-column-width), 100%);
	align-items: start;
}

.quotation-field-grid--grouped {
	grid-template-columns: repeat(12, minmax(0, 1fr));
	gap: 12px 16px;
	width: min(var(--editor-column-width), 100%);
}

.quotation-field-grid--grouped > .form-group {
	width: 100%;
}

.quotation-field-grid--grouped > .quotation-client-field {
	grid-column: span 4;
}

.quotation-field-grid--grouped > .quotation-service-field {
	grid-column: span 8;
}

.quotation-field-grid--grouped > .quotation-property-field {
	grid-column: span 12;
}

.quotation-field-grid--grouped > .quotation-scope-of-work-field {
	grid-column: span 12;
}

.quotation-field-grid--grouped > .quotation-client-address-field,
.quotation-field-grid--grouped > .quotation-site-address-field {
	grid-column: span 6;
}

.quotation-catalog-field > :deep(.preset-text-input) {
	width: 100%;
}

.quotation-field-grid .field-input,
.quotation-field-grid .table-textarea,
.quotation-field-grid :deep(.catalog-search-input) {
	width: 100%;
}

.quotation-field-grid :deep(.catalog-search-input .field-input) {
	width: 100%;
	height: var(--proposal-control-height);
	min-height: var(--proposal-control-height);
	padding: 6px 12px;
	border: var(--proposal-control-border);
	border-radius: var(--proposal-control-radius);
	background: var(--proposal-control-background);
	color: var(--text-color, #333);
	font-size: var(--document-input-font-size);
	font-family: inherit;
	box-shadow: var(--proposal-control-shadow);
	box-sizing: border-box;
}

.quotation-field-grid--grouped .table-textarea {
	min-height: 64px;
}

.assignment-details-grid,
.editor-details-grid {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 14px 16px;
	width: min(var(--editor-column-width), 100%);
	align-items: start;
}

.proposal-details-grid {
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.assignment-details-grid .field-input,
.assignment-details-grid .table-textarea,
.assignment-details-grid :deep(.catalog-search-input),
.editor-details-grid .field-input,
.editor-details-grid .table-textarea,
.editor-details-grid :deep(.catalog-search-input) {
	width: 100%;
}

.assignment-span-2,
.editor-span-2 {
	grid-column: span 2;
}

.assignment-span-all {
	grid-column: 1 / -1;
}

.quotation-wide-field {
	grid-column: 1 / -1;
}

.assignment-wide-field {
	grid-column: 1 / -1;
}

.quotation-wide-field .table-textarea {
	width: 100%;
	min-height: 64px;
}

/* Wide fields fill the 720px editor column when placed directly in a section. */
.editor-column-form .quotation-wide-field {
	width: 100%;
	align-self: stretch;
}

/* Wide fields fill the 720px editor column set on .assignment-form sections. */
.assignment-form .assignment-wide-field {
	width: 100%;
	align-self: stretch;
}

.assignment-form .assignment-wide-field .field-input,
.assignment-form .assignment-wide-field .table-textarea,
.assignment-form .assignment-wide-textarea {
	width: 100%;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
	margin: 0;
}

.form-group label,
.form-group span {
	font-size: 0.9rem;
	color: var(--text-color);
	font-weight: 500;
}

.field-input,
.table-input,
.table-textarea {
	border: var(--proposal-control-border);
	border-radius: var(--proposal-control-radius);
	background: var(--proposal-control-background);
	color: var(--text-color, #333);
	font-size: var(--document-input-font-size);
	font-family: inherit;
	box-shadow: var(--proposal-control-shadow);
	outline: none;
	box-sizing: border-box;
}

.field-input,
.table-input {
	width: var(--proposal-input-width);
	max-width: 100%;
	height: var(--proposal-control-height);
	min-height: var(--proposal-control-height);
	padding: 6px 12px;
	line-height: 1;
}

.form-group :deep(.catalog-search-input) {
	width: var(--proposal-input-width);
	max-width: 100%;
}

.form-group :deep(.catalog-search-input .field-input) {
	width: 100%;
	box-shadow: var(--proposal-control-shadow);
}

.proposal-details-grid > .form-group {
	width: 100%;
	min-width: 0;
}

.proposal-details-grid .proposal-client-field :deep(.catalog-search-input) {
	width: 100%;
	max-width: none;
}

.table-textarea {
	width: var(--proposal-description-width);
	max-width: 100%;
	min-height: 96px;
	padding: 8px 12px;
	line-height: 1.35;
	overflow: hidden;
	resize: none;
}

.bulleted-textarea {
	padding-left: 24px;
}

.assignment-task-textarea {
	min-height: 44px;
}

.compact-textarea {
	min-height: var(--proposal-control-height);
}

.proposal-properties-list,
.proposal-properties-empty {
	width: min(var(--editor-column-width), 100%);
}

.proposal-properties-list {
	display: flex;
	flex-direction: column;
}

.proposal-property-entry {
	position: relative;
	display: grid;
	grid-template-columns: minmax(0, 1fr);
	gap: 14px;
	padding: 0 0 20px;
}

.proposal-property-entry + .proposal-property-entry {
	padding-top: 20px;
	border-top: 1px solid var(--border-color-secondary, #ebeef1);
}

.proposal-property-entry:last-child {
	padding-bottom: 0;
}

.proposal-property-entry :deep(.catalog-search-input),
.proposal-property-entry .proposal-property-address {
	width: 100%;
}

.proposal-property-address {
	min-height: 54px;
}

.proposal-property-label-row {
	display: block;
	padding-right: 40px;
}

.proposal-property-entry .proposal-property-remove {
	position: absolute;
	top: -4px;
	right: 0;
	width: 28px;
	min-width: 28px;
	height: 28px;
	min-height: 28px;
	border: none;
	background: transparent;
	box-shadow: none;
}

.proposal-properties-empty {
	margin: 0;
	padding: 14px 16px;
	border: 1px dashed var(--border-color, #dfe3e8);
	border-radius: var(--proposal-control-radius);
	color: var(--text-color-secondary);
	font-size: 0.85rem;
}

.field-input:hover,
.table-input:hover,
.table-textarea:hover {
	border-color: var(--border-color-active);
}

.field-input:focus,
.table-input:focus,
.table-textarea:focus {
	border-color: var(--text-color, #333);
}

.disabled-input {
	color: var(--text-color);
	opacity: 1;
	background: var(--noc-background-secondary, #fafafb);
	box-shadow: none;
	cursor: default;
}

.editor-section {
	display: flex;
	flex-direction: column;
	gap: 8px;
	align-items: flex-start;
	max-width: 100%;
	background: transparent;
	border: none;
	border-radius: 0;
	padding: 0;
	box-shadow: none;
}

.toolbar-bottom-spacer {
	flex: 0 0 140px;
	width: 1px;
	height: 140px;
	pointer-events: none;
}

.section-heading {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 12px;
	min-height: 34px;
	max-width: 100%;
}

.section-heading--compact {
	margin-top: 2px;
}

.section-heading h3 {
	margin: 0;
	font-size: 1rem;
	font-weight: 700;
	color: var(--text-color);
}

.assignment-optional-section-heading {
	width: 100%;
}

.assignment-optional-toggle {
	display: inline-flex;
	align-items: center;
	gap: 8px;
}

.assignment-optional-toggle-label {
	color: var(--text-color-secondary, #6b7280);
	font-size: 0.78rem;
	font-weight: 500;
}

.assignment-section-toggle {
	position: relative;
	width: 30px;
	height: 18px;
	border: none;
	border-radius: 3px;
	background: #78788033;
	cursor: pointer;
	padding: 0;
	transition: background-color 0.25s ease;
}

.assignment-section-toggle-thumb {
	position: absolute;
	top: 0;
	left: 0;
	width: 15px;
	height: 18px;
	border-radius: 3px;
	background: #fff;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15), 0 1px 1px rgba(0, 0, 0, 0.1);
	transition: transform 0.25s ease;
}

.assignment-section-toggle.is-on {
	background: #007aff;
}

.assignment-section-toggle.is-on .assignment-section-toggle-thumb {
	transform: translateX(15px);
}

.assignment-section-toggle:focus-visible {
	outline: 2px solid #007aff;
	outline-offset: 2px;
}

.editor-table {
	width: fit-content;
	max-width: 100%;
	table-layout: auto;
	border-collapse: separate;
	border-spacing: 0;
	background: var(--proposal-control-background);
	border: var(--proposal-control-border);
	border-radius: var(--proposal-control-radius);
	box-shadow: var(--proposal-control-shadow);
	overflow: hidden;
}

.editor-table + .editor-table {
	margin-top: 8px;
}

.editor-table th,
.editor-table td {
	padding: var(--proposal-table-cell-gap);
	text-align: left;
	vertical-align: top;
	font-size: 0.85rem;
}

.editor-table th {
	width: 170px;
	background: var(--noc-background-secondary, #f8f9fa);
	color: var(--text-color-secondary);
	font-weight: 600;
}

.editor-table thead th {
	font-size: 0.8rem;
}

.editor-table .table-input,
.editor-table .table-textarea,
.editor-table .icon-btn {
	box-shadow: none;
}

.editor-table .icon-btn:hover:not(:disabled) {
	box-shadow: none;
}

.proposal-summary-textarea {
	width: 100%;
	min-height: var(--proposal-control-height);
}

.document-footer-textarea {
	width: min(720px, 100%);
	min-height: 64px;
}

.editor-table tbody td {
	padding: calc(var(--proposal-table-cell-gap) / 4) calc(var(--proposal-table-cell-gap) / 2);
}

.editor-table tbody td:first-child {
	padding-left: var(--proposal-table-cell-gap);
}

.editor-table tbody td:last-child {
	padding-right: var(--proposal-table-cell-gap);
}

/* Keep the table's inner top/bottom breathing room while rows stay tight. */
.editor-table tbody tr:first-child td {
	padding-top: var(--proposal-table-cell-gap);
}

.editor-table tbody tr:last-child td {
	padding-bottom: var(--proposal-table-cell-gap);
}

.editor-table thead + tbody tr:first-child td {
	padding-top: calc(var(--proposal-table-cell-gap) / 4);
}

.schedule-table,
.schedule-table td,
.acceptance-table,
.acceptance-table td {
	background: #fff;
}

.row-format-cell {
	position: relative;
}

/* A quiet scan line passes over the window while the document is being checked. */
.document-check-scan {
	position: fixed;
	inset: 0;
	z-index: 80;
	background: rgba(10, 97, 201, 0.025);
	pointer-events: none;
}

.document-check-scan-line {
	position: absolute;
	top: 0;
	right: 0;
	left: 0;
	height: 2px;
	background: linear-gradient(90deg, transparent, rgba(10, 97, 201, 0.55) 30%, rgba(10, 97, 201, 0.55) 70%, transparent);
	box-shadow: 0 0 12px rgba(10, 97, 201, 0.35);
	animation: document-check-scan 1.6s ease-in-out infinite alternate;
}

@keyframes document-check-scan {
	to {
		transform: translateY(calc(100vh - 2px));
	}
}

@media (prefers-reduced-motion: reduce) {
	.document-check-scan-line {
		display: none;
	}
}

.schedule-block-chip {
	position: absolute;
	top: 50%;
	right: 8px;
	transform: translateY(-50%);
	padding: 2px 6px;
	border-radius: 4px;
	background: var(--noc-blue-10);
	color: var(--noc-blue);
	font: 600 11px/14px var(--font-family-base);
	pointer-events: none;
}

.document-editor--refreshed .row-format-table .table-input.is-block-row {
	padding-right: 56px;
}

/* The total row's value is calculated, so it reads as fixed text. */
.document-editor--refreshed .row-format-table .table-input.is-total-value {
	background: var(--noc-background-secondary);
	cursor: default;
}

.proposal-block-pricing {
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
}

.document-editor--refreshed .pricing-blocks-table {
	width: 100%;
	table-layout: fixed;
}

/* Wins over the shared editor table rules (auto widths, cells aligned to the top). */
.document-editor--refreshed .editor-column-form .editor-table.pricing-blocks-table th:nth-child(2) {
	width: 17%;
}

.document-editor--refreshed .editor-column-form .editor-table.pricing-blocks-table th:nth-child(3) {
	width: 19%;
}

.document-editor--refreshed .editor-column-form .editor-table.pricing-blocks-table th:nth-child(4) {
	width: 21%;
}

.document-editor--refreshed .editor-column-form .editor-table.pricing-blocks-table :is(th, td) {
	vertical-align: middle;
}

.document-editor--refreshed .editor-column-form .pricing-blocks-table tbody tr td.pricing-block-cell {
	padding: 10px 12px;
	background: var(--noc-background-secondary);
	font: 400 14px/20px var(--font-family-base, sans-serif);
	overflow-wrap: anywhere;
}

.document-editor--refreshed .pricing-blocks-table .numeric {
	text-align: right;
	font-variant-numeric: tabular-nums;
}

.pricing-blocks-hint {
	margin: 0;
	color: var(--text-color-secondary);
	font-size: 0.8125rem;
}

/* Rows show their format as the PDF prints it. */
.document-editor--refreshed .row-format-table .table-input.is-bold {
	font-weight: 700;
}

.document-editor--refreshed .row-format-table .table-input.is-italic {
	font-style: italic;
}

.document-editor--refreshed .row-format-table .table-input.is-blue {
	color: #1f4e79;
}

.quotation-schedule-table thead th:nth-child(1) {
	width: 17%;
}

.quotation-schedule-table thead th:nth-child(2) {
	width: 24%;
}

.quotation-cost-table thead th:nth-child(2) {
	width: 22%;
}

.quotation-cost-table .quotation-amount-input {
	text-align: right;
}

.editor-column-form .editor-section {
	width: min(var(--editor-column-width), 100%);
	scroll-margin-top: 16px;
}

.assignment-editor-panel,
.proposal-editor-panel,
.quotation-editor-panel {
	scroll-margin-top: 16px;
}

.outline-anchor {
	scroll-margin-top: 16px;
}

.editor-column-form .editor-section + .editor-section {
	border-top: 1px solid var(--border-color-secondary, #ebeef1);
	padding-top: 20px;
}

.editor-column-form .editor-table,
.proposal-editor-panel .editor-table {
	width: min(var(--editor-column-width), 100%);
	table-layout: fixed;
}

.editor-column-form .editor-table th,
.proposal-editor-panel .editor-table th {
	width: auto;
}

.editor-column-form .editor-table .action-col,
.proposal-editor-panel .editor-table .action-col {
	width: 48px;
}

.editor-column-form .editor-table .move-col,
.proposal-editor-panel .editor-table .move-col {
	width: 44px;
	text-align: center;
	vertical-align: middle;
}

.editor-table tbody td.move-col {
	padding-left: calc(var(--proposal-table-cell-gap) / 2);
	padding-right: calc(var(--proposal-table-cell-gap) / 2);
}

.editor-column-form .text-sections-table tbody td.move-col {
	vertical-align: top;
}

.row-move-handle {
	width: 30px;
	min-width: 30px;
	height: 30px;
	min-height: 30px;
	border-color: transparent;
	background: transparent;
	box-shadow: none;
	color: var(--text-color-secondary, #6b7280);
	cursor: grab;
	touch-action: none;
}

.row-move-handle:active,
.proposal-reorder-row.is-row-dragging .row-move-handle {
	cursor: grabbing;
}

.proposal-reorder-row {
	transition: opacity 0.12s ease;
}

.proposal-reorder-row.is-row-dragging {
	opacity: 0.58;
}

.editor-column-form .editor-table .table-input,
.proposal-editor-panel .editor-table .table-input {
	width: 100%;
}

.editor-column-form .editor-table .table-textarea,
.proposal-editor-panel .editor-table .table-textarea {
	width: 100%;
}

/* Column proportions for proposal tables (fixed layout). */
.service-areas-table thead th:first-child {
	width: 34%;
}

.tasks-table thead th:nth-child(1) {
	width: 24%;
}

.tasks-table thead th:nth-child(3) {
	width: 22%;
}

.text-sections-table thead th:nth-child(2),
.scope-clarification-table thead th:nth-child(1) {
	width: 30%;
}

.signatory-table thead th:nth-child(1),
.signatory-table thead th:nth-child(2) {
	width: 27%;
}

.quotation-totals-grid {
	max-width: 720px;
	grid-template-columns: minmax(180px, 0.6fr) minmax(300px, 1fr);
}

.assignment-pricing-grid {
	width: min(var(--editor-column-width), 100%);
	max-width: none;
	grid-template-columns: minmax(320px, 1fr) minmax(280px, 0.9fr);
}

.editor-column-form .pricing-notes-group,
.editor-column-form .pricing-overrides {
	max-width: var(--editor-column-width);
}

.editor-table th.action-col,
.editor-table td.action-col {
	width: auto;
	min-width: 0;
	text-align: left;
	vertical-align: top;
}

.pricing-grid {
	display: grid;
	grid-template-columns: minmax(0, 1.5fr) minmax(0, 1fr);
	gap: 16px;
	width: 100%;
	max-width: 880px;
	align-items: stretch;
}

.pricing-card {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 16px;
	background: var(--proposal-control-background);
	border: var(--proposal-control-border);
	border-radius: 12px;
	box-shadow: var(--proposal-control-shadow);
	box-sizing: border-box;
}

.pricing-card--summary {
	background: var(--noc-background-secondary, #f8f9fa);
}

.pricing-card__title {
	margin: 0;
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: var(--text-color-secondary);
}

.pricing-field-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	gap: 12px 16px;
}

.pricing-field-grid .field-input {
	width: 100%;
	box-shadow: none;
}

.pricing-field-grid__wide {
	grid-column: 1 / -1;
}

.pricing-stats {
	display: flex;
	flex-direction: column;
	flex: 1;
	margin: 0;
}

.pricing-stat {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 16px;
	padding: 9px 0;
	border-bottom: 1px solid var(--border-color);
}

.pricing-stat:last-child,
.pricing-stat:has(+ .pricing-stat--total) {
	border-bottom: none;
}

.pricing-stat dt {
	font-size: 0.85rem;
	font-weight: 500;
	color: var(--text-color-secondary);
}

.pricing-stat dd {
	margin: 0;
	font-size: 0.92rem;
	font-weight: 700;
	color: var(--text-color);
	white-space: nowrap;
}

.pricing-stat--total {
	margin-top: auto;
	padding: 12px 14px;
	border-bottom: none;
	border-radius: 8px;
	background: var(--noc-blue, #0065ff);
}

.pricing-stat--total dt,
.pricing-stat--total dd {
	color: #fff;
}

.pricing-stat--total dd {
	font-size: 1.02rem;
}

.pricing-notes-group {
	width: 100%;
	max-width: 880px;
	margin-top: 8px;
}

.pricing-notes-group__heading {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	gap: 12px;
	margin-bottom: 4px;
}

.pricing-notes-group__heading > label {
	font-size: 0.9rem;
	font-weight: 500;
	color: var(--text-color);
}

.pricing-notes-group .pricing-notes-textarea {
	width: 100%;
	min-height: var(--proposal-control-height);
}

.pricing-overrides {
	width: 100%;
	max-width: 880px;
	margin-top: 8px;
	border: var(--proposal-control-border);
	border-radius: 12px;
	background: var(--proposal-control-background);
	box-shadow: var(--proposal-control-shadow);
	overflow: hidden;
	box-sizing: border-box;
}

.pricing-overrides > summary {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	font-size: 0.85rem;
	font-weight: 600;
	color: var(--text-color);
	cursor: pointer;
	list-style: none;
	user-select: none;
}

.pricing-overrides > summary::-webkit-details-marker {
	display: none;
}

.pricing-overrides > summary::before {
	content: '';
	width: 7px;
	height: 7px;
	border-right: 2px solid var(--text-color-secondary);
	border-bottom: 2px solid var(--text-color-secondary);
	transform: rotate(-45deg);
	transition: transform 0.15s ease;
}

.pricing-overrides[open] > summary::before {
	transform: rotate(45deg);
}

.pricing-overrides__body {
	display: flex;
	flex-direction: column;
	gap: 14px;
	padding: 16px;
	border-top: 1px solid var(--border-color);
}

.pricing-overrides__hint {
	margin: 0;
	font-size: 0.8rem;
	color: var(--text-color-secondary);
}

.pricing-overrides__field-hint {
	font-size: 0.75rem;
	color: var(--text-color-secondary);
}

.floating-action-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 7px;
	white-space: nowrap;
	border: none;
	background: transparent;
	color: var(--noc-blue, #0065ff);
	cursor: pointer;
	font-weight: 600;
	padding: 0;
	border-radius: 0;
	height: 32px;
	min-height: 32px;
	box-shadow: none;
	transition: color 0.1s ease, opacity 0.1s ease;
	font-size: 0.86rem;
	white-space: nowrap;
}

.icon-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: var(--proposal-control-height);
	min-width: var(--proposal-control-height);
	height: var(--proposal-control-height);
	min-height: var(--proposal-control-height);
	padding: 0;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: #fff;
	color: var(--text-color-secondary);
	cursor: pointer;
	box-shadow: var(--box-shadow);
	transition: background-color 0.1s ease, border-color 0.1s ease, color 0.1s ease, box-shadow 0.1s ease;
}

.icon-btn--add {
	width: auto;
	min-width: var(--proposal-control-height);
	gap: 8px;
	padding: 0;
	border: none;
	border-radius: 0;
	background: transparent;
	color: var(--noc-blue, #0065ff);
	box-shadow: none;
	font-size: 0.9rem;
	font-weight: 600;
}

.icon-btn--danger {
	color: var(--noc-red, #d93025);
}

/* Keep destructive controls quiet until the row is engaged. */
.editor-table tbody tr .icon-btn--danger {
	opacity: 0;
	transition: opacity 0.12s ease;
}

.editor-table tbody tr:hover .icon-btn--danger,
.editor-table tbody tr:focus-within .icon-btn--danger {
	opacity: 1;
}

@media (hover: none) {
	.editor-table tbody tr .icon-btn--danger {
		opacity: 1;
	}
}

.floating-action-btn:hover:not(:disabled) {
	background: transparent;
	border-color: transparent;
	color: var(--noc-blue, #0065ff);
	box-shadow: none;
	opacity: 0.82;
}

.icon-btn:hover:not(:disabled) {
	background: var(--noc-background-secondary, #f8f9fa);
	border-color: var(--border-color-active, #cfd6df);
	color: var(--text-color, #333);
	box-shadow: 0 1px 3px rgba(27, 31, 35, 0.12);
}

.icon-btn--add:hover:not(:disabled) {
	background: transparent;
	border-color: transparent;
	color: var(--noc-blue, #0065ff);
	box-shadow: none;
}

.icon-btn--danger:hover:not(:disabled) {
	background: rgba(217, 48, 37, 0.08);
	border-color: rgba(217, 48, 37, 0.35);
	color: var(--noc-red, #d93025);
}

.floating-action-btn:focus-visible,
.icon-btn:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.28);
	outline-offset: 2px;
}

.floating-action-btn:active:not(:disabled),
.icon-btn:active:not(:disabled) {
	transform: none;
}

.floating-action-btn:disabled,
.icon-btn:disabled {
	cursor: not-allowed;
	opacity: 0.55;
}

.action-icon {
	width: 1rem;
	height: 1rem;
	font-size: 1rem;
}

.icon-btn--add .action-icon {
	width: 1.3rem;
	height: 1.3rem;
	font-size: 1.3rem;
	stroke-width: 3.2;
}

.action-label {
	line-height: 1;
	white-space: nowrap;
}

.floating-action-btn--primary {
	background: var(--noc-blue, #0a61c9);
	color: #fff;
	font-weight: 600;
	padding: 0 18px;
	border-radius: 9px;
	height: 36px;
	min-height: 36px;
	box-shadow: 0 1px 2px rgba(10, 97, 201, 0.35);
}

.floating-action-btn--primary:hover:not(:disabled) {
	background: var(--primary-hover, #0855b3);
	color: #fff;
	border-color: transparent;
	box-shadow: 0 2px 6px rgba(10, 97, 201, 0.4);
	opacity: 1;
}

.footer-action-icon {
	width: 1.25rem;
	height: 1.25rem;
	font-size: 1.25rem;
	stroke-width: 2.8;
	flex: 0 0 auto;
}

.editor-outline {
	position: fixed;
	top: 118px;
	right: 28px;
	z-index: 40;
	display: flex;
	flex-direction: column;
	width: 180px;
	max-height: calc(100vh - 240px);
	overflow-y: auto;
}

.editor-outline__title {
	margin: 0 0 8px;
	padding: 0 14px;
	font-size: 0.68rem;
	font-weight: 700;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--text-color-secondary);
}

.editor-outline__link {
	display: block;
	width: 100%;
	padding: 5px 14px;
	border: none;
	border-left: 2px solid var(--border-color-secondary, #ebeef1);
	background: transparent;
	color: var(--text-color-secondary);
	font-size: 0.8rem;
	font-weight: 500;
	text-align: left;
	line-height: 1.3;
	cursor: pointer;
	transition: color 0.12s ease, border-color 0.12s ease;
}

.editor-outline__link:hover {
	color: var(--text-color);
}

.editor-outline__link.is-active {
	border-left-color: var(--noc-blue, #0a61c9);
	color: var(--noc-blue, #0a61c9);
	font-weight: 600;
}

.editor-outline__link:focus-visible {
	outline: 2px solid rgba(0, 101, 255, 0.28);
	outline-offset: -2px;
}

@media (max-width: 1360px) {
	.editor-outline {
		display: none;
	}
}

.unsaved-pill {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
	min-height: 32px;
	padding: 0 10px;
	border-radius: 8px;
	border: 1px solid rgba(192, 86, 33, 0.2);
	background: rgba(255, 247, 237, 0.92);
	color: #c05621;
	font-size: 0.8rem;
	font-weight: 600;
}

/* The pill says whether the document is saved: client documents save on their own, supplier
   documents on Save. */
.unsaved-pill--saving,
.unsaved-pill--saved {
	border-color: var(--border-color);
	background: rgba(255, 255, 255, 0.92);
	color: var(--text-color-secondary);
}

.unsaved-pill--saved {
	color: var(--report-status-success-color);
}

.unsaved-pill--error {
	border-color: rgba(180, 35, 24, 0.25);
	background: rgba(254, 243, 242, 0.92);
	color: #b42318;
}

.unsaved-pill--retry {
	font-family: inherit;
	cursor: pointer;
	touch-action: manipulation;
}

.unsaved-pill--retry:focus-visible {
	outline: 2px solid rgba(180, 35, 24, 0.28);
	outline-offset: 2px;
}

@media (hover: hover) {
	.unsaved-pill--retry:hover {
		background: rgba(254, 228, 226, 0.96);
	}
}

.error-inline {
	color: var(--noc-orange, #c0392b);
	font-size: 0.85rem;
	font-weight: 600;
	align-self: center;
	max-width: 280px;
}

.loading-state {
	padding: 24px;
	text-align: center;
	color: var(--text-color-secondary);
}

@media (max-width: 980px) {
	:deep(.creator-footer) {
		left: 16px;
		right: 16px;
		bottom: 14px;
		transform: none;
		width: auto;
		max-width: none;
		justify-content: center;
		flex-wrap: wrap;
	}

	.floating-action-btn {
		flex: 1 1 180px;
	}
}

@media (max-width: 820px) {
	.assignment-section-toggle {
		width: 36px;
		height: 20px;
	}

	.assignment-section-toggle-thumb {
		width: 16px;
		height: 16px;
	}

	.assignment-section-toggle.is-on .assignment-section-toggle-thumb {
		transform: translateX(14px);
	}
}

@media (max-width: 760px) {
	:deep(.invoice-creator.client-document-editor-layout) {
		padding-bottom: 180px;
	}

	.toolbar-bottom-spacer {
		flex-basis: 220px;
		height: 220px;
	}

	.field-input,
	.table-input,
	.table-textarea {
		width: 100%;
	}

	.form-group :deep(.catalog-search-input),
	.quotation-field-grid,
	.assignment-details-grid,
	.editor-details-grid,
	.assignment-wide-field .field-input,
	.assignment-wide-textarea,
	.quotation-wide-field .table-textarea {
		width: 100%;
	}

	.quotation-field-grid,
	.assignment-details-grid,
	.editor-details-grid {
		grid-template-columns: 1fr;
	}

	.assignment-span-2,
	.editor-span-2,
	.quotation-field-grid--grouped > .form-group,
	.quotation-field-grid--grouped > .quotation-address-field {
		grid-column: auto;
	}

	.document-footer-textarea {
		width: 100%;
	}

	.pricing-grid {
		grid-template-columns: 1fr;
	}

	.editor-table,
	.editor-table thead,
	.editor-table tbody,
	.editor-table tr,
	.editor-table th,
	.editor-table td {
		display: block;
		width: 100%;
		box-sizing: border-box;
	}

	.editor-table thead {
		display: none;
	}

	.editor-table th,
	.editor-table td {
		border-bottom: none;
	}

	.action-col {
		text-align: left;
	}

	.editor-table .move-col {
		width: 100%;
		text-align: left;
	}

	.editor-table .move-col--empty {
		display: none;
	}
}
</style>

<style scoped src="@/views/client-documents/document-editor.css"></style>
